import { AdmLogoManager } from './AdmLogoManager';
import { useAuth } from '../../hooks/useAuth';
import { useFlowaesStore } from '../../store/useFlowaesStore';
















import { cn, formatLastActive } from '../../lib/utils';
import { loginWithGoogle } from '../../lib/firebase';
import { isAdminEmailLabel } from '../../lib/admin';
import { 
  ShieldCheck, X, Lock, Unlock, RefreshCcw, SlidersHorizontal, 
  Trash2, Activity, Search, MoreHorizontal, MessageSquarePlus, TrendingUp, Filter
} from 'lucide-react';

interface GodPanelProps {
  auth: ReturnType<typeof useAuth>;
    isCreator: boolean;
  isAdmin: boolean;
  handleSaveBrandIdentity: () => void;
  saveSettings?: () => void;
  resetSettings?: () => void;
  isSaving?: boolean;
  useRealData?: boolean;
  setUseRealData?: (v: boolean) => void;
  symbol?: string;
  setSymbol?: (v: string) => void;
}


export function GodPanel({
  auth,
  isCreator,
  isAdmin,
  handleSaveBrandIdentity,
  saveSettings,
  resetSettings,
  isSaving,
  useRealData = false,
  setUseRealData = () => {},
  symbol = 'BTCUSDT',
  setSymbol = () => {}
}: GodPanelProps) {
  const uiHiddenLevel = useFlowaesStore(s => s.uiHiddenLevel);
  const showGodPanel = useFlowaesStore(s => s.showGodPanel);
  const setShowGodPanel = useFlowaesStore(s => s.setShowGodPanel);
  const isDraggingGodSlider = useFlowaesStore(s => s.isDraggingGodSlider);
  const activeGodTab = useFlowaesStore(s => s.activeGodTab);
  const setActiveGodTab = useFlowaesStore(s => s.setActiveGodTab);

  if (!isCreator || !showGodPanel || uiHiddenLevel === 2) return null;

  return (
    <div 
      className={`god-panel absolute top-0 right-0 h-full w-[350px] max-w-full z-[1000] bg-[#02040a]/95 border-l border-[#00ffa3]/30 shadow-[-10px_0_40px_rgba(0,0,0,0.8)] backdrop-blur-xl flex flex-col select-none animate-in slide-in-from-right duration-300 ${isDraggingGodSlider ? "god-panel-dragging" : ""}`}
    >
      <div className="flex justify-between items-center p-4 border-b border-[#00ffa3]/20 bg-[#00ffa3]/5">
        <h2 className="text-[#00ffa3] font-bold tracking-[2px] uppercase text-[12px] flex items-center gap-2">
          Painel God
        </h2>
        <button onClick={() => setShowGodPanel(false)} className="text-[#00ffa3]/50 hover:text-[#00ffa3] transition-colors">X</button>
      </div>
      
      <div className="flex gap-1 p-2 border-b border-white/5 overflow-x-auto custom-scrollbar">
        {(['general', 'dashboard', 'physics', 'colors', 'aesthetics', 'spatial', 'users', 'feedbacks'] as const).map(tab => (
          <button 
            key={tab}
            onClick={() => setActiveGodTab(tab)}
            className={`px-3 py-1.5 text-[9px] uppercase tracking-[1px] rounded transition-colors whitespace-nowrap ${activeGodTab === tab ? 'bg-[#00ffa3]/20 text-[#00ffa3]' : 'text-[#666] hover:text-[#00ffa3]/50'}`}
          >
            {tab}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar p-4 relative">
        {activeGodTab === 'general' && <GeneralTab auth={auth} isAdmin={isAdmin} isCreator={isCreator} />}
        {activeGodTab === 'dashboard' && <DashboardTab auth={auth} isAdmin={isAdmin} isCreator={isCreator} />}
        {activeGodTab === 'physics' && <PhysicsTab auth={auth} isAdmin={isAdmin} isCreator={isCreator} />}
        {activeGodTab === 'colors' && <ColorsTab auth={auth} isAdmin={isAdmin} isCreator={isCreator} />}
        {activeGodTab === 'aesthetics' && <AestheticsTab auth={auth} isAdmin={isAdmin} isCreator={isCreator} />}
        {activeGodTab === 'spatial' && <SpatialTab auth={auth} isAdmin={isAdmin} isCreator={isCreator} />}
        {activeGodTab === 'users' && <UsersTab auth={auth} isAdmin={isAdmin} isCreator={isCreator} />}
        {activeGodTab === 'feedbacks' && <FeedbacksTab auth={auth} isAdmin={isAdmin} isCreator={isCreator} />}
      </div>
    </div>
  );
}

function PhysicsTab({ auth, isAdmin, isCreator }: { auth: NonNullable<GodPanelProps["auth"]>, isAdmin: boolean, isCreator: boolean }) {
  const {
    adminMetrics, leadsList, feedbacksList, expandedFeedbackId, setExpandedFeedbackId,
    isFeedbacksLoading, setFeedbackToDelete, systemUsersList,
    isSystemUsersLoading, systemUsersFilter, setSystemUsersFilter, userLevelFilter,
    setUserLevelFilter, showUserFilters, setShowUserFilters,
    setEditingSystemUser, expandedSystemUserId, setExpandedSystemUserId,
    activeSystemUserMenu, setActiveSystemUserMenu, showActivitiesForUserId,
    setShowActivitiesForUserId, setUserFeedbacksModalId,
    isSavingBrand, brandSaveSuccess, brandSaveError, fetchFeedbacks,
    fetchSystemUsers, fetchLeads, handleUpdateUserStatus,
    handleDeleteUser, handleDeleteLead, fetchAdminMetrics,
    isLeadsLoading
  } = auth || {};

  const colorIntensity = useFlowaesStore(s => s.colorIntensity);
  const colorContrast = useFlowaesStore(s => s.colorContrast);
  const momentumSensitivity = useFlowaesStore(s => s.momentumSensitivity);
  const lineWidth3D = useFlowaesStore(s => s.lineWidth3D);
  const setColorIntensity = useFlowaesStore(s => s.setColorIntensity);
  const setColorContrast = useFlowaesStore(s => s.setColorContrast);
  const setMomentumSensitivity = useFlowaesStore(s => s.setMomentumSensitivity);
  const setLineWidth3D = useFlowaesStore(s => s.setLineWidth3D);
  const curveTension = useFlowaesStore(s => s.curveTension);
  const setCurveTension = useFlowaesStore(s => s.setCurveTension);
  const lockedSliders = useFlowaesStore(s => s.lockedSliders);
  const setLockedSliders = useFlowaesStore(s => s.setLockedSliders);
  return (
    <>
      
<div className="space-y-4 animate-in fade-in duration-200">
                {/* Bloom Slider */}
                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                      Intensidade Bloom (Brilho)
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#00ffa3] font-bold">{colorIntensity.toFixed(1)}x</span>
                      <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ['colorIntensity']: !(p['colorIntensity'] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                        {(lockedSliders['colorIntensity'] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                      </button>
                    </div>
                  </div>
                  <input
                    type="range"
                    min="0.5"
                    max="3"
                    step="0.1"
                    value={colorIntensity}
                    onChange={(e) => setColorIntensity(parseFloat(e.target.value))}
                    disabled={lockedSliders['colorIntensity'] ?? false}
                    className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['colorIntensity'] ?? false) ? "opacity-50 cursor-not-allowed" : "")}
                  />
                  <p className="text-[7px] text-[#444] leading-relaxed">Ajusta o fator de emissão neon das linhas helicoidais no espaço 3D.</p>
                </div>

                {/* Filament Thickness */}
                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                      Espessura do Filamento (3D)
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#00ffa3] font-bold">{lineWidth3D.toFixed(1)}px</span>
                      <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ['lineWidth3D']: !(p['lineWidth3D'] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                        {(lockedSliders['lineWidth3D'] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                      </button>
                    </div>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    step="0.5"
                    value={lineWidth3D}
                    onChange={(e) => setLineWidth3D(parseFloat(e.target.value))}
                    disabled={lockedSliders['lineWidth3D'] ?? false}
                    className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['lineWidth3D'] ?? false) ? "opacity-50 cursor-not-allowed" : "")}
                  />
                  <p className="text-[7px] text-[#444] leading-relaxed">Controla o diâmetro em pixels do tubo de renderização da hélice.</p>
                </div>

                {/* Curve Tension */}
                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                      Tensão da Curva (Catmull Tension)
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#00ffa3] font-bold">{curveTension.toFixed(2)}</span>
                      <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ['curveTension']: !(p['curveTension'] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                        {(lockedSliders['curveTension'] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                      </button>
                    </div>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="1.2"
                    step="0.05"
                    value={curveTension}
                    onChange={(e) => setCurveTension(parseFloat(e.target.value))}
                    disabled={lockedSliders['curveTension'] ?? false}
                    className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['curveTension'] ?? false) ? "opacity-50 cursor-not-allowed" : "")}
                  />
                  <p className="text-[7px] text-[#444] leading-relaxed">Suaviza ou estressa a transição entre nós temporais adjacentes da curva.</p>
                </div>

                {/* Color Contrast */}
                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                      Contraste de Cores
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#00ffa3] font-bold">{colorContrast.toFixed(2)}</span>
                      <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ['colorContrast']: !(p['colorContrast'] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                        {(lockedSliders['colorContrast'] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                      </button>
                    </div>
                  </div>
                  <input
                    type="range"
                    min="0.5"
                    max="2.5"
                    step="0.05"
                    value={colorContrast}
                    onChange={(e) => setColorContrast(parseFloat(e.target.value))}
                    disabled={lockedSliders['colorContrast'] ?? false}
                    className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['colorContrast'] ?? false) ? "opacity-50 cursor-not-allowed" : "")}
                  />
                  <p className="text-[7px] text-[#444] leading-relaxed">Ajusta a vivacidade e separação cromática dos gráficos.</p>
                </div>

                {/* Momentum Sensitivity */}
                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                      Sensibilidade de Momentum
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#00ffa3] font-bold">{momentumSensitivity.toFixed(1)}</span>
                      <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ['momentumSensitivity']: !(p['momentumSensitivity'] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                        {(lockedSliders['momentumSensitivity'] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                      </button>
                    </div>
                  </div>
                  <input
                    type="range"
                    min="0.5"
                    max="5.0"
                    step="0.1"
                    value={momentumSensitivity}
                    onChange={(e) => setMomentumSensitivity(parseFloat(e.target.value))}
                    disabled={lockedSliders['momentumSensitivity'] ?? false}
                    className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['momentumSensitivity'] ?? false) ? "opacity-50 cursor-not-allowed" : "")}
                  />
                  <p className="text-[7px] text-[#444] leading-relaxed">Controla a resposta do cálculo de oscilação do gráfico.</p>
                </div>
              </div>
            
    </>
  );
}

function ColorsTab({ auth, isAdmin, isCreator }: { auth: NonNullable<GodPanelProps["auth"]>, isAdmin: boolean, isCreator: boolean }) {
  const {
    adminMetrics, leadsList, feedbacksList, expandedFeedbackId, setExpandedFeedbackId,
    isFeedbacksLoading, setFeedbackToDelete, systemUsersList,
    isSystemUsersLoading, systemUsersFilter, setSystemUsersFilter, userLevelFilter,
    setUserLevelFilter, showUserFilters, setShowUserFilters,
    setEditingSystemUser, expandedSystemUserId, setExpandedSystemUserId,
    activeSystemUserMenu, setActiveSystemUserMenu, showActivitiesForUserId,
    setShowActivitiesForUserId, setUserFeedbacksModalId,
    isSavingBrand, brandSaveSuccess, brandSaveError, fetchFeedbacks,
    fetchSystemUsers, fetchLeads, handleUpdateUserStatus,
    handleDeleteUser, handleDeleteLead, fetchAdminMetrics,
    isLeadsLoading
  } = auth || {};

  const colorUp = useFlowaesStore(s => s.colorUp);
  const colorDown = useFlowaesStore(s => s.colorDown);
  const buyersColor = useFlowaesStore(s => s.buyersColor);
  const sellersColor = useFlowaesStore(s => s.sellersColor);
  const setColorUp = useFlowaesStore(s => s.setColorUp);
  const setColorDown = useFlowaesStore(s => s.setColorDown);
  const setBuyersColor = useFlowaesStore(s => s.setBuyersColor);
  const setSellersColor = useFlowaesStore(s => s.setSellersColor);
  const gridColor = useFlowaesStore(s => s.gridColor);
  const edgesColor = useFlowaesStore(s => s.edgesColor);
  const periodColor = useFlowaesStore(s => s.periodColor);
  const setGridColor = useFlowaesStore(s => s.setGridColor);
  const setEdgesColor = useFlowaesStore(s => s.setEdgesColor);
  const setPeriodColor = useFlowaesStore(s => s.setPeriodColor);
  const midLineWallColor = useFlowaesStore(s => s.midLineWallColor);
  const setMidLineWallColor = useFlowaesStore(s => s.setMidLineWallColor);
  const radialMenuColor = useFlowaesStore(s => s.radialMenuColor);
  const buttonBaseColor = useFlowaesStore(s => s.buttonBaseColor);
  const setRadialMenuColor = useFlowaesStore(s => s.setRadialMenuColor);
  const setButtonBaseColor = useFlowaesStore(s => s.setButtonBaseColor);
  return (
    <>
      
<div className="space-y-4 animate-in fade-in duration-200">
                {/* Core Trend Colors */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px] block">Cor de Alta (Compradores)</span>
                    <div className="flex items-center gap-2 bg-[#05070e] border border-white/5 rounded p-1.5">
                      <input type="color" value={colorUp} onChange={(e) => setColorUp(e.target.value)} className="w-6 h-6 rounded cursor-pointer bg-transparent border-0 p-0" />
                      <span className="text-[9px] font-mono text-[#00ffa3] uppercase">{colorUp}</span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px] block">Cor de Baixa (Vendedores)</span>
                    <div className="flex items-center gap-2 bg-[#05070e] border border-white/5 rounded p-1.5">
                      <input type="color" value={colorDown} onChange={(e) => setColorDown(e.target.value)} className="w-6 h-6 rounded cursor-pointer bg-transparent border-0 p-0" />
                      <span className="text-[9px] font-mono text-[#ff3d67] uppercase">{colorDown}</span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px] block">Cor Radial Menu</span>
                    <div className="flex items-center gap-2 bg-[#05070e] border border-white/5 rounded p-1.5">
                      <input type="color" value={radialMenuColor} onChange={(e) => setRadialMenuColor(e.target.value)} className="w-6 h-6 rounded cursor-pointer bg-transparent border-0 p-0" />
                      <span className="text-[9px] font-mono text-[#00ffa3] uppercase">{radialMenuColor}</span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px] block">Cor de Botões</span>
                    <div className="flex items-center gap-2 bg-[#05070e] border border-white/5 rounded p-1.5">
                      <input type="color" value={buttonBaseColor} onChange={(e) => setButtonBaseColor(e.target.value)} className="w-6 h-6 rounded cursor-pointer bg-transparent border-0 p-0" />
                      <span className="text-[9px] font-mono text-[#00ffa3] uppercase">{buttonBaseColor}</span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px] block">Cor Arestas</span>
                    <div className="flex items-center gap-2 bg-[#05070e] border border-white/5 rounded p-1.5">
                      <input type="color" value={edgesColor} onChange={(e) => setEdgesColor(e.target.value)} className="w-6 h-6 rounded cursor-pointer bg-transparent border-0 p-0" />
                      <span className="text-[9px] font-mono text-white uppercase">{edgesColor}</span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px] block">Cor Grade 3D</span>
                    <div className="flex items-center gap-2 bg-[#05070e] border border-white/5 rounded p-1.5">
                      <input type="color" value={gridColor} onChange={(e) => setGridColor(e.target.value)} className="w-6 h-6 rounded cursor-pointer bg-transparent border-0 p-0" />
                      <span className="text-[9px] font-mono text-[#00d9ff] uppercase">{gridColor}</span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px] block">Cor Períodos</span>
                    <div className="flex items-center gap-2 bg-[#05070e] border border-white/5 rounded p-1.5">
                      <input type="color" value={periodColor} onChange={(e) => setPeriodColor(e.target.value)} className="w-6 h-6 rounded cursor-pointer bg-transparent border-0 p-0" />
                      <span className="text-[9px] font-mono text-white uppercase">{periodColor}</span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px] block">Cor Linha Média</span>
                    <div className="flex items-center gap-2 bg-[#05070e] border border-white/5 rounded p-1.5">
                      <input type="color" value={midLineWallColor} onChange={(e) => setMidLineWallColor(e.target.value)} className="w-6 h-6 rounded cursor-pointer bg-transparent border-0 p-0" />
                      <span className="text-[9px] font-mono text-white uppercase">{midLineWallColor}</span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px] block">Txt Compradores</span>
                    <div className="flex items-center gap-2 bg-[#05070e] border border-white/5 rounded p-1.5">
                      <input type="color" value={buyersColor} onChange={(e) => setBuyersColor(e.target.value)} className="w-6 h-6 rounded cursor-pointer bg-transparent border-0 p-0" />
                      <span className="text-[9px] font-mono text-[#00ffa3] uppercase">{buyersColor}</span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px] block">Txt Vendedores</span>
                    <div className="flex items-center gap-2 bg-[#05070e] border border-white/5 rounded p-1.5">
                      <input type="color" value={sellersColor} onChange={(e) => setSellersColor(e.target.value)} className="w-6 h-6 rounded cursor-pointer bg-transparent border-0 p-0" />
                      <span className="text-[9px] font-mono text-[#ff3d67] uppercase">{sellersColor}</span>
                    </div>
                  </div>
                </div>

              </div>
            
    </>
  );
}

function AestheticsTab({ auth, isAdmin, isCreator }: { auth: NonNullable<GodPanelProps["auth"]>, isAdmin: boolean, isCreator: boolean }) {
  const {
    adminMetrics, leadsList, feedbacksList, expandedFeedbackId, setExpandedFeedbackId,
    isFeedbacksLoading, setFeedbackToDelete, systemUsersList,
    isSystemUsersLoading, systemUsersFilter, setSystemUsersFilter, userLevelFilter,
    setUserLevelFilter, showUserFilters, setShowUserFilters,
    setEditingSystemUser, expandedSystemUserId, setExpandedSystemUserId,
    activeSystemUserMenu, setActiveSystemUserMenu, showActivitiesForUserId,
    setShowActivitiesForUserId, setUserFeedbacksModalId,
    isSavingBrand, brandSaveSuccess, brandSaveError, fetchFeedbacks,
    fetchSystemUsers, fetchLeads, handleUpdateUserStatus,
    handleDeleteUser, handleDeleteLead, fetchAdminMetrics,
    isLeadsLoading
  } = auth || {};

  const showSMA10 = useFlowaesStore(s => s.showSMA10);
  const showSMA20 = useFlowaesStore(s => s.showSMA20);
  const showSMA50 = useFlowaesStore(s => s.showSMA50);
  const sma10Period = useFlowaesStore(s => s.sma10Period);
  const sma20Period = useFlowaesStore(s => s.sma20Period);
  const sma50Period = useFlowaesStore(s => s.sma50Period);
  const setShowSMA10 = useFlowaesStore(s => s.setShowSMA10);
  const setShowSMA20 = useFlowaesStore(s => s.setShowSMA20);
  const setShowSMA50 = useFlowaesStore(s => s.setShowSMA50);
  const setSma10Period = useFlowaesStore(s => s.setSma10Period);
  const setSma20Period = useFlowaesStore(s => s.setSma20Period);
  const setSma50Period = useFlowaesStore(s => s.setSma50Period);
  const gridOpacity = useFlowaesStore(s => s.gridOpacity);
  const gridThickness = useFlowaesStore(s => s.gridThickness);
  const showGrid = useFlowaesStore(s => s.showGrid);
  const edgesOpacity = useFlowaesStore(s => s.edgesOpacity);
  const titleSize = useFlowaesStore(s => s.titleSize);
  const floorLabelsSize = useFlowaesStore(s => s.floorLabelsSize);
  const sideLabelsSize = useFlowaesStore(s => s.sideLabelsSize);
  const floorLabelsSpread = useFlowaesStore(s => s.floorLabelsSpread);
  const sideLabelsSpread = useFlowaesStore(s => s.sideLabelsSpread);
  const floorLabelsOpacity = useFlowaesStore(s => s.floorLabelsOpacity);
  const sideLabelsOpacity = useFlowaesStore(s => s.sideLabelsOpacity);
  const timeLabelsSize = useFlowaesStore(s => s.timeLabelsSize);
  const timeLabelsOpacity = useFlowaesStore(s => s.timeLabelsOpacity);
  const momentumLabelsSize = useFlowaesStore(s => s.momentumLabelsSize);
  const momentumLabelsOpacity = useFlowaesStore(s => s.momentumLabelsOpacity);
  const priceLabelsSize = useFlowaesStore(s => s.priceLabelsSize);
  const priceLabelsOpacity = useFlowaesStore(s => s.priceLabelsOpacity);
  const periodLabelsSize = useFlowaesStore(s => s.periodLabelsSize);
  const periodLabelsOpacity = useFlowaesStore(s => s.periodLabelsOpacity);
  const periodOpacity = useFlowaesStore(s => s.periodOpacity);
  const showPeriodSeparators = useFlowaesStore(s => s.showPeriodSeparators);
  const periodLineStyle = useFlowaesStore(s => s.periodLineStyle);
  const periodLineWidth = useFlowaesStore(s => s.periodLineWidth);
  const showPeriodLabels = useFlowaesStore(s => s.showPeriodLabels);
  const setGridOpacity = useFlowaesStore(s => s.setGridOpacity);
  const setGridThickness = useFlowaesStore(s => s.setGridThickness);
  const setShowGrid = useFlowaesStore(s => s.setShowGrid);
  const setEdgesOpacity = useFlowaesStore(s => s.setEdgesOpacity);
  const setTitleSize = useFlowaesStore(s => s.setTitleSize);
  const setFloorLabelsSize = useFlowaesStore(s => s.setFloorLabelsSize);
  const setSideLabelsSize = useFlowaesStore(s => s.setSideLabelsSize);
  const setFloorLabelsSpread = useFlowaesStore(s => s.setFloorLabelsSpread);
  const setSideLabelsSpread = useFlowaesStore(s => s.setSideLabelsSpread);
  const setFloorLabelsOpacity = useFlowaesStore(s => s.setFloorLabelsOpacity);
  const setSideLabelsOpacity = useFlowaesStore(s => s.setSideLabelsOpacity);
  const setTimeLabelsSize = useFlowaesStore(s => s.setTimeLabelsSize);
  const setTimeLabelsOpacity = useFlowaesStore(s => s.setTimeLabelsOpacity);
  const setMomentumLabelsSize = useFlowaesStore(s => s.setMomentumLabelsSize);
  const setMomentumLabelsOpacity = useFlowaesStore(s => s.setMomentumLabelsOpacity);
  const setPriceLabelsSize = useFlowaesStore(s => s.setPriceLabelsSize);
  const setPriceLabelsOpacity = useFlowaesStore(s => s.setPriceLabelsOpacity);
  const setPeriodLabelsSize = useFlowaesStore(s => s.setPeriodLabelsSize);
  const setPeriodLabelsOpacity = useFlowaesStore(s => s.setPeriodLabelsOpacity);
  const setPeriodOpacity = useFlowaesStore(s => s.setPeriodOpacity);
  const setShowPeriodSeparators = useFlowaesStore(s => s.setShowPeriodSeparators);
  const setPeriodLineStyle = useFlowaesStore(s => s.setPeriodLineStyle);
  const setPeriodLineWidth = useFlowaesStore(s => s.setPeriodLineWidth);
  const setShowPeriodLabels = useFlowaesStore(s => s.setShowPeriodLabels);
  const connectorsOpacity = useFlowaesStore(s => s.connectorsOpacity);
  const connectorsThickness = useFlowaesStore(s => s.connectorsThickness);
  const showConnectors = useFlowaesStore(s => s.showConnectors);
  const backdropBlur = useFlowaesStore(s => s.backdropBlur);
  const floorChartOpacity = useFlowaesStore(s => s.floorChartOpacity);
  const sideChartOpacity = useFlowaesStore(s => s.sideChartOpacity);
  const backChartOpacity = useFlowaesStore(s => s.backChartOpacity);
  const floorSmaOpacity = useFlowaesStore(s => s.floorSmaOpacity);
  const sideSmaOpacity = useFlowaesStore(s => s.sideSmaOpacity);
  const backSmaOpacity = useFlowaesStore(s => s.backSmaOpacity);
  const main3DOpacity = useFlowaesStore(s => s.main3DOpacity);
  const sma103DOpacity = useFlowaesStore(s => s.sma103DOpacity);
  const sma203DOpacity = useFlowaesStore(s => s.sma203DOpacity);
  const sma503DOpacity = useFlowaesStore(s => s.sma503DOpacity);
  const floorChartLineWidth = useFlowaesStore(s => s.floorChartLineWidth);
  const sideChartLineWidth = useFlowaesStore(s => s.sideChartLineWidth);
  const backSmaLineWidth = useFlowaesStore(s => s.backSmaLineWidth);
  const floorSmaLineWidth = useFlowaesStore(s => s.floorSmaLineWidth);
  const sideSmaLineWidth = useFlowaesStore(s => s.sideSmaLineWidth);
  const showMidLineWall = useFlowaesStore(s => s.showMidLineWall);
  const midLineWallOpacity = useFlowaesStore(s => s.midLineWallOpacity);
  const sideHistoryShadowStrength = useFlowaesStore(s => s.sideHistoryShadowStrength);
  const historyShadowStrength = useFlowaesStore(s => s.historyShadowStrength);
  const setConnectorsOpacity = useFlowaesStore(s => s.setConnectorsOpacity);
  const setConnectorsThickness = useFlowaesStore(s => s.setConnectorsThickness);
  const setShowConnectors = useFlowaesStore(s => s.setShowConnectors);
  const setBackdropBlur = useFlowaesStore(s => s.setBackdropBlur);
  const setFloorChartOpacity = useFlowaesStore(s => s.setFloorChartOpacity);
  const setSideChartOpacity = useFlowaesStore(s => s.setSideChartOpacity);
  const setBackChartOpacity = useFlowaesStore(s => s.setBackChartOpacity);
  const setFloorSmaOpacity = useFlowaesStore(s => s.setFloorSmaOpacity);
  const setSideSmaOpacity = useFlowaesStore(s => s.setSideSmaOpacity);
  const setBackSmaOpacity = useFlowaesStore(s => s.setBackSmaOpacity);
  const setMain3DOpacity = useFlowaesStore(s => s.setMain3DOpacity);
  const setSma103DOpacity = useFlowaesStore(s => s.setSma103DOpacity);
  const setSma203DOpacity = useFlowaesStore(s => s.setSma203DOpacity);
  const setSma503DOpacity = useFlowaesStore(s => s.setSma503DOpacity);
  const setFloorChartLineWidth = useFlowaesStore(s => s.setFloorChartLineWidth);
  const setSideChartLineWidth = useFlowaesStore(s => s.setSideChartLineWidth);
  const setBackSmaLineWidth = useFlowaesStore(s => s.setBackSmaLineWidth);
  const setFloorSmaLineWidth = useFlowaesStore(s => s.setFloorSmaLineWidth);
  const setSideSmaLineWidth = useFlowaesStore(s => s.setSideSmaLineWidth);
  const setShowMidLineWall = useFlowaesStore(s => s.setShowMidLineWall);
  const setMidLineWallOpacity = useFlowaesStore(s => s.setMidLineWallOpacity);
  const setSideHistoryShadowStrength = useFlowaesStore(s => s.setSideHistoryShadowStrength);
  const setHistoryShadowStrength = useFlowaesStore(s => s.setHistoryShadowStrength);
  const lockedSliders = useFlowaesStore(s => s.lockedSliders);
  const setLockedSliders = useFlowaesStore(s => s.setLockedSliders);
  return (
    <>
      
<div className="space-y-4 animate-in fade-in duration-200">
                <AdmLogoManager />
                {/* Sliders para Opacidades e Tamanhos */}
                
                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                      Desfoque Traseiro (UI Backdrop Blur)
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#00ffa3] font-bold">{backdropBlur}px</span>
                      <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ['backdropBlur']: !(p['backdropBlur'] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                        {(lockedSliders['backdropBlur'] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                      </button>
                    </div>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="30"
                    step="1"
                    value={backdropBlur}
                    onChange={(e) => setBackdropBlur(parseFloat(e.target.value))}
                    disabled={lockedSliders['backdropBlur'] ?? false}
                    className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['backdropBlur'] ?? false) ? "opacity-50 cursor-not-allowed" : "")}
                  />
                </div>

                
                  

                
                  

                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                      Opacidade Gráfico de Chão
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#00ffa3] font-bold">{Math.round(floorChartOpacity * 100)}%</span>
                      <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ['floorChartOpacity']: !(p['floorChartOpacity'] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                        {(lockedSliders['floorChartOpacity'] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                      </button>
                    </div>
                  </div>
<input type="range" min="0" max="1" step="0.01" value={floorChartOpacity} onChange={(e) => setFloorChartOpacity(parseFloat(e.target.value))} disabled={lockedSliders['floorChartOpacity'] ?? false} className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['floorChartOpacity'] ?? false) ? "opacity-50 cursor-not-allowed" : "")} />
                </div>
                
                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                      Opacidade Gráfico Lateral
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#00ffa3] font-bold">{Math.round(sideChartOpacity * 100)}%</span>
                      <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ['sideChartOpacity']: !(p['sideChartOpacity'] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                        {(lockedSliders['sideChartOpacity'] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                      </button>
                    </div>
                  </div>
<input type="range" min="0" max="1" step="0.01" value={sideChartOpacity} onChange={(e) => setSideChartOpacity(parseFloat(e.target.value))} disabled={lockedSliders['sideChartOpacity'] ?? false} className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['sideChartOpacity'] ?? false) ? "opacity-50 cursor-not-allowed" : "")} />
                </div>
                
                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                      Opacidade Gráfico Central (Costas)
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#00ffa3] font-bold">{Math.round(backChartOpacity * 100)}%</span>
                      <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ['backChartOpacity']: !(p['backChartOpacity'] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                        {(lockedSliders['backChartOpacity'] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                      </button>
                    </div>
                  </div>
<input type="range" min="0" max="1" step="0.01" value={backChartOpacity} onChange={(e) => setBackChartOpacity(parseFloat(e.target.value))} disabled={lockedSliders['backChartOpacity'] ?? false} className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['backChartOpacity'] ?? false) ? "opacity-50 cursor-not-allowed" : "")} />
                </div>

                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                      Sombra do Gráfico 3D (Escurecimento de Histórico)
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#00ffa3] font-bold">{Math.round(historyShadowStrength * 100)}%</span>
                      <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ['historyShadowStrength']: !(p['historyShadowStrength'] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                        {(lockedSliders['historyShadowStrength'] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                      </button>
                    </div>
                  </div>
<input type="range" min="0" max="1" step="0.01" value={historyShadowStrength} onChange={(e) => setHistoryShadowStrength(parseFloat(e.target.value))} disabled={lockedSliders['historyShadowStrength'] ?? false} className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['historyShadowStrength'] ?? false) ? "opacity-50 cursor-not-allowed" : "")} />
                </div>

                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                      Sombra do Gráfico Lateral (Escurecimento de Histórico)
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#00ffa3] font-bold">{Math.round(sideHistoryShadowStrength * 100)}%</span>
                      <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ['sideHistoryShadowStrength']: !(p['sideHistoryShadowStrength'] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                        {(lockedSliders['sideHistoryShadowStrength'] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                      </button>
                    </div>
                  </div>
<input type="range" min="0" max="1" step="0.01" value={sideHistoryShadowStrength} onChange={(e) => setSideHistoryShadowStrength(parseFloat(e.target.value))} disabled={lockedSliders['sideHistoryShadowStrength'] ?? false} className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['sideHistoryShadowStrength'] ?? false) ? "opacity-50 cursor-not-allowed" : "")} />
                </div>
                
                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                      Opacidade Linha Principal 3D
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#00ffa3] font-bold">{Math.round(main3DOpacity * 100)}%</span>
                      <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ['main3DOpacity']: !(p['main3DOpacity'] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                        {(lockedSliders['main3DOpacity'] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                      </button>
                    </div>
                  </div>
<input type="range" min="0" max="1" step="0.01" value={main3DOpacity} onChange={(e) => setMain3DOpacity(parseFloat(e.target.value))} disabled={lockedSliders['main3DOpacity'] ?? false} className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['main3DOpacity'] ?? false) ? "opacity-50 cursor-not-allowed" : "")} />
                </div>
                
                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                      Opacidade SMA 10 (3D)
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#00ffa3] font-bold">{Math.round(sma103DOpacity * 100)}%</span>
                      <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ['sma103DOpacity']: !(p['sma103DOpacity'] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                        {(lockedSliders['sma103DOpacity'] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                      </button>
                    </div>
                  </div>
<input type="range" min="0" max="1" step="0.01" value={sma103DOpacity} onChange={(e) => setSma103DOpacity(parseFloat(e.target.value))} disabled={lockedSliders['sma103DOpacity'] ?? false} className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['sma103DOpacity'] ?? false) ? "opacity-50 cursor-not-allowed" : "")} />
                </div>

                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                      Opacidade SMA 20 (3D)
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#00ffa3] font-bold">{Math.round(sma203DOpacity * 100)}%</span>
                      <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ['sma203DOpacity']: !(p['sma203DOpacity'] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                        {(lockedSliders['sma203DOpacity'] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                      </button>
                    </div>
                  </div>
<input type="range" min="0" max="1" step="0.01" value={sma203DOpacity} onChange={(e) => setSma203DOpacity(parseFloat(e.target.value))} disabled={lockedSliders['sma203DOpacity'] ?? false} className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['sma203DOpacity'] ?? false) ? "opacity-50 cursor-not-allowed" : "")} />
                </div>

                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                      Opacidade SMA 50 (3D)
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#00ffa3] font-bold">{Math.round(sma503DOpacity * 100)}%</span>
                      <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ['sma503DOpacity']: !(p['sma503DOpacity'] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                        {(lockedSliders['sma503DOpacity'] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                      </button>
                    </div>
                  </div>
<input type="range" min="0" max="1" step="0.01" value={sma503DOpacity} onChange={(e) => setSma503DOpacity(parseFloat(e.target.value))} disabled={lockedSliders['sma503DOpacity'] ?? false} className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['sma503DOpacity'] ?? false) ? "opacity-50 cursor-not-allowed" : "")} />
                </div>

                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                      Opacidade SMA de Chão
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#00ffa3] font-bold">{Math.round(floorSmaOpacity * 100)}%</span>
                      <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ['floorSmaOpacity']: !(p['floorSmaOpacity'] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                        {(lockedSliders['floorSmaOpacity'] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                      </button>
                    </div>
                  </div>
<input type="range" min="0" max="1" step="0.01" value={floorSmaOpacity} onChange={(e) => setFloorSmaOpacity(parseFloat(e.target.value))} disabled={lockedSliders['floorSmaOpacity'] ?? false} className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['floorSmaOpacity'] ?? false) ? "opacity-50 cursor-not-allowed" : "")} />
                </div>
                
                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                      Opacidade SMA Lateral
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#00ffa3] font-bold">{Math.round(sideSmaOpacity * 100)}%</span>
                      <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ['sideSmaOpacity']: !(p['sideSmaOpacity'] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                        {(lockedSliders['sideSmaOpacity'] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                      </button>
                    </div>
                  </div>
<input type="range" min="0" max="1" step="0.01" value={sideSmaOpacity} onChange={(e) => setSideSmaOpacity(parseFloat(e.target.value))} disabled={lockedSliders['sideSmaOpacity'] ?? false} className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['sideSmaOpacity'] ?? false) ? "opacity-50 cursor-not-allowed" : "")} />
                </div>

                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                      Opacidade SMA Central (Costas)
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#00ffa3] font-bold">{Math.round(backSmaOpacity * 100)}%</span>
                      <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ['backSmaOpacity']: !(p['backSmaOpacity'] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                        {(lockedSliders['backSmaOpacity'] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                      </button>
                    </div>
                  </div>
<input type="range" min="0" max="1" step="0.01" value={backSmaOpacity} onChange={(e) => setBackSmaOpacity(parseFloat(e.target.value))} disabled={lockedSliders['backSmaOpacity'] ?? false} className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['backSmaOpacity'] ?? false) ? "opacity-50 cursor-not-allowed" : "")} />
                </div>
                
                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                      Grossura Gráfico de Chão
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#00ffa3] font-bold">{floorChartLineWidth.toFixed(1)}px</span>
                      <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ['floorChartLineWidth']: !(p['floorChartLineWidth'] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                        {(lockedSliders['floorChartLineWidth'] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                      </button>
                    </div>
                  </div>
<input type="range" min="0.1" max="10" step="0.1" value={floorChartLineWidth} onChange={(e) => setFloorChartLineWidth(parseFloat(e.target.value))} disabled={lockedSliders['floorChartLineWidth'] ?? false} className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['floorChartLineWidth'] ?? false) ? "opacity-50 cursor-not-allowed" : "")} />
                </div>

                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                      Grossura Gráfico Lateral
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#00ffa3] font-bold">{sideChartLineWidth.toFixed(1)}px</span>
                      <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ['sideChartLineWidth']: !(p['sideChartLineWidth'] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                        {(lockedSliders['sideChartLineWidth'] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                      </button>
                    </div>
                  </div>
<input type="range" min="0.1" max="10" step="0.1" value={sideChartLineWidth} onChange={(e) => setSideChartLineWidth(parseFloat(e.target.value))} disabled={lockedSliders['sideChartLineWidth'] ?? false} className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['sideChartLineWidth'] ?? false) ? "opacity-50 cursor-not-allowed" : "")} />
                </div>

                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                      Grossura SMA de Chão
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#00ffa3] font-bold">{floorSmaLineWidth.toFixed(1)}px</span>
                      <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ['floorSmaLineWidth']: !(p['floorSmaLineWidth'] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                        {(lockedSliders['floorSmaLineWidth'] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                      </button>
                    </div>
                  </div>
<input type="range" min="0.1" max="10" step="0.1" value={floorSmaLineWidth} onChange={(e) => setFloorSmaLineWidth(parseFloat(e.target.value))} disabled={lockedSliders['floorSmaLineWidth'] ?? false} className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['floorSmaLineWidth'] ?? false) ? "opacity-50 cursor-not-allowed" : "")} />
                </div>

                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                      Grossura SMA Lateral
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#00ffa3] font-bold">{sideSmaLineWidth.toFixed(1)}px</span>
                      <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ['sideSmaLineWidth']: !(p['sideSmaLineWidth'] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                        {(lockedSliders['sideSmaLineWidth'] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                      </button>
                    </div>
                  </div>
<input type="range" min="0.1" max="10" step="0.1" value={sideSmaLineWidth} onChange={(e) => setSideSmaLineWidth(parseFloat(e.target.value))} disabled={lockedSliders['sideSmaLineWidth'] ?? false} className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['sideSmaLineWidth'] ?? false) ? "opacity-50 cursor-not-allowed" : "")} />
                </div>

                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                      Grossura SMA Central (Costas)
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#00ffa3] font-bold">{backSmaLineWidth.toFixed(1)}px</span>
                      <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ['backSmaLineWidth']: !(p['backSmaLineWidth'] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                        {(lockedSliders['backSmaLineWidth'] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                      </button>
                    </div>
                  </div>
<input type="range" min="0.1" max="10" step="0.1" value={backSmaLineWidth} onChange={(e) => setBackSmaLineWidth(parseFloat(e.target.value))} disabled={lockedSliders['backSmaLineWidth'] ?? false} className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['backSmaLineWidth'] ?? false) ? "opacity-50 cursor-not-allowed" : "")} />
                </div>
                
                {/* Grade 3D (Grid) Controls */}
                <div className="border border-white/5 bg-[#0a0e27]/40 rounded p-2.5 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                      Mostrar Grade 3D
                    </span>
                    <input 
                      type="checkbox" 
                      checked={showGrid} 
                      onChange={(e) => setShowGrid(e.target.checked)} 
                      className="rounded bg-[#111] border-white/10 text-[#00ffa3] focus:ring-[#00ffa3]/20"
                    />
                  </div>
                  {showGrid && (
                    <>
                      <div className="space-y-1">
                        <div className="flex justify-between items-center">
                          <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                            Opacidade da Grade
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] text-[#00ffa3] font-bold">{Math.round((gridOpacity || 0) * 100)}%</span>
                            <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ['gridOpacity']: !(p['gridOpacity'] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                              {(lockedSliders['gridOpacity'] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                            </button>
                          </div>
                        </div>
<input type="range" min="0" max="1" step="0.01" value={gridOpacity} onChange={(e) => setGridOpacity(parseFloat(e.target.value))} disabled={lockedSliders['gridOpacity'] ?? false} className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['gridOpacity'] ?? false) ? "opacity-50 cursor-not-allowed" : "")} />
                      </div>
                      <div className="space-y-1">
                        <div className="flex justify-between items-center">
                          <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">Espessura da Grade</span>
                          <span className="text-[10px] text-[#00ffa3] font-bold">{gridThickness?.toFixed(1) || "1.5"}px</span>
                        </div>
                        <input type="range" min="0.5" max="5" step="0.1" value={gridThickness} onChange={(e) => setGridThickness(parseFloat(e.target.value))} className="w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]" />
                      </div>
                    </>
                  )}
                </div>

                {/* Linha Média da Parede (MidLine Wall) Controls */}
                <div className="border border-white/5 bg-[#0a0e27]/40 rounded p-2.5 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                      Mostrar Linha Média (Parede)
                    </span>
                    <input 
                      type="checkbox" 
                      checked={showMidLineWall} 
                      onChange={(e) => setShowMidLineWall(e.target.checked)} 
                      className="rounded bg-[#111] border-white/10 text-[#00ffa3] focus:ring-[#00ffa3]/20"
                    />
                  </div>
                  {showMidLineWall && (
                    <div className="space-y-1">
                      <div className="flex justify-between items-center">
                        <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                          Opacidade Linha Média
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] text-[#00ffa3] font-bold">{Math.round((midLineWallOpacity || 0) * 100)}%</span>
                          <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ['midLineWallOpacity']: !(p['midLineWallOpacity'] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                            {(lockedSliders['midLineWallOpacity'] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                          </button>
                        </div>
                      </div>
<input type="range" min="0" max="1" step="0.01" value={midLineWallOpacity} onChange={(e) => setMidLineWallOpacity(parseFloat(e.target.value))} disabled={lockedSliders['midLineWallOpacity'] ?? false} className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['midLineWallOpacity'] ?? false) ? "opacity-50 cursor-not-allowed" : "")} />
                    </div>
                  )}
                </div>

                {/* Períodos de Tempo (Period Separators) Controls */}
                <div className="border border-white/5 bg-[#0a0e27]/40 rounded p-2.5 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                      Mostrar Separadores de Período
                    </span>
                    <div className="flex items-center gap-2">
                      <input 
                        type="checkbox" 
                        checked={showPeriodSeparators} 
                        onChange={(e) => setShowPeriodSeparators(e.target.checked)} 
                        disabled={lockedSliders['box_periods'] ?? false}
                        className="rounded bg-[#111] border-white/10 text-[#00ffa3] focus:ring-[#00ffa3]/20"
                      />
                      <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ['box_periods']: !(p['box_periods'] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                        {(lockedSliders['box_periods'] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                      </button>
                    </div>
                  </div>
                  {showPeriodSeparators && (
                    <div className="space-y-2 pt-1 border-t border-white/5">
                      <div className="space-y-1">
                        <div className="flex justify-between items-center">
                          <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                            Opacidade Linha de Período
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] text-[#00ffa3] font-bold">{Math.round((periodOpacity || 0) * 100)}%</span>
                            </div>
                          </div>
                          <input type="range" min="0" max="1" step="0.01" value={periodOpacity} onChange={(e) => setPeriodOpacity(parseFloat(e.target.value))} disabled={lockedSliders['box_periods'] ?? false} className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['box_periods'] ?? false) ? "opacity-50 cursor-not-allowed" : "")} />
                      </div>
                      
                      <div className="space-y-1 pt-1">
                        <div className="flex justify-between items-center">
                          <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">Estilo da Linha</span>
                          <select value={periodLineStyle} onChange={(e) => setPeriodLineStyle(e.target.value as 'solid' | 'dashed')} disabled={lockedSliders['box_periods'] ?? false} className={cn("bg-[#111] border border-white/10 text-white text-[9px] rounded px-1.5 py-0.5", (lockedSliders['box_periods'] ?? false) ? "opacity-50 cursor-not-allowed" : "")}>
                            <option value="solid">Sólida</option>
                            <option value="dashed">Tracejada</option>
                          </select>
                        </div>
                      </div>
                      
                      <div className="space-y-1 pt-1">
                        <div className="flex justify-between items-center">
                          <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                            Espessura da Linha
                          </span>
                          <span className="text-[10px] text-[#00ffa3] font-bold">{periodLineWidth?.toFixed(1) || "1.0"}px</span>
                        </div>
                      </div>
                      <input type="range" min="0.1" max="10" step="0.1" value={periodLineWidth} onChange={(e) => setPeriodLineWidth(parseFloat(e.target.value))} disabled={lockedSliders['box_periods'] ?? false} className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['box_periods'] ?? false) ? "opacity-50 cursor-not-allowed" : "")} />

                      <div className="flex justify-between items-center pt-2 border-t border-white/5">
                        <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                          Mostrar Etiquetas de Período
                        </span>
                        <input 
                          type="checkbox" 
                          checked={showPeriodLabels} 
                          onChange={(e) => setShowPeriodLabels(e.target.checked)} 
                          disabled={lockedSliders['box_periods'] ?? false}
                          className="rounded bg-[#111] border-white/10 text-[#00ffa3] focus:ring-[#00ffa3]/20"
                        />
                      </div>
                      
                      {showPeriodLabels && (
                        <>
                          <div className="space-y-1 pt-1">
                            <div className="flex justify-between items-center">
                              <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                                Opacidade das Etiquetas
                              </span>
                              <span className="text-[10px] text-[#00ffa3] font-bold">{Math.round((periodLabelsOpacity || 0) * 100)}%</span>
                            </div>
                          </div>
                          <input type="range" min="0" max="1" step="0.01" value={periodLabelsOpacity} onChange={(e) => setPeriodLabelsOpacity(parseFloat(e.target.value))} disabled={lockedSliders['box_periods'] ?? false} className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['box_periods'] ?? false) ? "opacity-50 cursor-not-allowed" : "")} />
                          <div className="space-y-1 pt-1">
                            <div className="flex justify-between items-center">
                              <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                                Tamanho das Etiquetas
                              </span>
                              <span className="text-[10px] text-[#00ffa3] font-bold">{periodLabelsSize}px</span>
                            </div>
                          </div>
                          <input type="range" min="1" max="100" step="1" value={periodLabelsSize} onChange={(e) => setPeriodLabelsSize(parseFloat(e.target.value))} disabled={lockedSliders['box_periods'] ?? false} className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['box_periods'] ?? false) ? "opacity-50 cursor-not-allowed" : "")} />
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Estudos & Indicadores Controls */}
                <div className="border border-white/5 bg-[#0a0e27]/40 rounded p-2.5 space-y-2">
                  <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px] block border-b border-white/5 pb-1">Estudos & Indicadores</span>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1 bg-[#05070e] p-1.5 rounded border border-white/5">
                      <div className="flex justify-between items-center">
                        <span className="text-[8px] text-[#facc15] font-bold">SMA 1</span>
                        <input type="checkbox" checked={showSMA10} onChange={(e) => setShowSMA10(e.target.checked)} className="rounded bg-[#111] text-[#facc15]" />
                      </div>
                      {showSMA10 && (
                        <input type="number" value={sma10Period} onChange={(e) => setSma10Period(Math.max(1, parseInt(e.target.value) || 1))} className="w-full bg-[#111] text-[#facc15] rounded px-1.5 py-0.5 text-[9px] text-center" />
                      )}
                    </div>
                    <div className="space-y-1 bg-[#05070e] p-1.5 rounded border border-white/5">
                      <div className="flex justify-between items-center">
                        <span className="text-[8px] text-[#38bdf8] font-bold">SMA 2</span>
                        <input type="checkbox" checked={showSMA20} onChange={(e) => setShowSMA20(e.target.checked)} className="rounded bg-[#111] text-[#38bdf8]" />
                      </div>
                      {showSMA20 && (
                        <input type="number" value={sma20Period} onChange={(e) => setSma20Period(Math.max(1, parseInt(e.target.value) || 1))} className="w-full bg-[#111] text-[#38bdf8] rounded px-1.5 py-0.5 text-[9px] text-center" />
                      )}
                    </div>
                    <div className="space-y-1 bg-[#05070e] p-1.5 rounded border border-white/5">
                      <div className="flex justify-between items-center">
                        <span className="text-[8px] text-[#a855f7] font-bold">SMA 3</span>
                        <input type="checkbox" checked={showSMA50} onChange={(e) => setShowSMA50(e.target.checked)} className="rounded bg-[#111] text-[#a855f7]" />
                      </div>
                      {showSMA50 && (
                        <input type="number" value={sma50Period} onChange={(e) => setSma50Period(Math.max(1, parseInt(e.target.value) || 1))} className="w-full bg-[#111] text-[#a855f7] rounded px-1.5 py-0.5 text-[9px] text-center" />
                      )}
                    </div>
                    <div className="space-y-1 bg-[#05070e] p-1.5 rounded border border-white/5 flex items-center justify-between">
                    </div>
                  </div>
                </div>

                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                      Opacidade Arestas (Edges)
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#00ffa3] font-bold">{Math.round(edgesOpacity * 100)}%</span>
                      <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ['edgesOpacity']: !(p['edgesOpacity'] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                        {(lockedSliders['edgesOpacity'] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                      </button>
                    </div>
                  </div>
<input type="range" min="0" max="1" step="0.01" value={edgesOpacity} onChange={(e) => setEdgesOpacity(parseFloat(e.target.value))} disabled={lockedSliders['edgesOpacity'] ?? false} className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['edgesOpacity'] ?? false) ? "opacity-50 cursor-not-allowed" : "")} />
                </div>
                
                            <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                      Opacidade Textos Chão (C/V)
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#00ffa3] font-bold">{Math.round((floorLabelsOpacity || 0) * 100)}%</span>
                      <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ['floorLabelsOpacity']: !(p['floorLabelsOpacity'] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                        {(lockedSliders['floorLabelsOpacity'] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                      </button>
                    </div>
                  </div>
<input type="range" min="0" max="1" step="0.01" value={floorLabelsOpacity} onChange={(e) => setFloorLabelsOpacity(parseFloat(e.target.value))} disabled={lockedSliders['floorLabelsOpacity'] ?? false} className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['floorLabelsOpacity'] ?? false) ? "opacity-50 cursor-not-allowed" : "")} />
                </div>

                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                      Opacidade Textos Lateral (C/V)
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#00ffa3] font-bold">{Math.round((sideLabelsOpacity || 0) * 100)}%</span>
                      <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ['sideLabelsOpacity']: !(p['sideLabelsOpacity'] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                        {(lockedSliders['sideLabelsOpacity'] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                      </button>
                    </div>
                  </div>
<input type="range" min="0" max="1" step="0.01" value={sideLabelsOpacity} onChange={(e) => setSideLabelsOpacity(parseFloat(e.target.value))} disabled={lockedSliders['sideLabelsOpacity'] ?? false} className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['sideLabelsOpacity'] ?? false) ? "opacity-50 cursor-not-allowed" : "")} />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">Tamanho Título 3D</span>
                    <input type="number" value={titleSize} onChange={(e) => setTitleSize(parseFloat(e.target.value))} className="w-full bg-[#111] text-[#00ffa3] rounded px-2 py-1 text-[10px]" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">Tamanho Textos Chão</span>
                    <input type="number" value={floorLabelsSize} onChange={(e) => setFloorLabelsSize(parseFloat(e.target.value))} className="w-full bg-[#111] text-[#00ffa3] rounded px-2 py-1 text-[10px]" />
                  </div>
                  <div className="space-y-1">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">Tamanho Textos Lateral</span>
                    <input type="number" value={sideLabelsSize} onChange={(e) => setSideLabelsSize(parseFloat(e.target.value))} className="w-full bg-[#111] text-[#00ffa3] rounded px-2 py-1 text-[10px]" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <div className="flex justify-between items-center">
                      <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">Distância Chão (C/V)</span>
                      <div className="flex gap-2 items-center">
                        <span className="text-[#00ffa3] text-[9px] font-mono">{floorLabelsSpread.toFixed(2)}</span>
                        <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ["floorLabelsSpread"]: !(p["floorLabelsSpread"] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                          {(lockedSliders["floorLabelsSpread"] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                        </button>
                      </div>
                    </div>
<input type="range" min="0.05" max="2" step="0.05" value={floorLabelsSpread} onChange={(e) => setFloorLabelsSpread(parseFloat(e.target.value))} disabled={lockedSliders["floorLabelsSpread"] ?? false} className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders["floorLabelsSpread"] ?? false) ? "opacity-50 cursor-not-allowed" : "")} />
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between items-center">
                      <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">Espaçamento Lateral</span>
                      <div className="flex gap-2 items-center">
                        <span className="text-[#00ffa3] text-[9px] font-mono">{sideLabelsSpread.toFixed(2)}</span>
                        <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ["sideLabelsSpread"]: !(p["sideLabelsSpread"] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                          {(lockedSliders["sideLabelsSpread"] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                        </button>
                      </div>
                    </div>
<input type="range" min="0.05" max="2" step="0.05" value={sideLabelsSpread} onChange={(e) => setSideLabelsSpread(parseFloat(e.target.value))} disabled={lockedSliders["sideLabelsSpread"] ?? false} className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders["sideLabelsSpread"] ?? false) ? "opacity-50 cursor-not-allowed" : "")} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">Tamanho Etiqueta (Data)</span>
                    <input type="number" value={timeLabelsSize} onChange={(e) => setTimeLabelsSize(parseFloat(e.target.value))} className="w-full bg-[#111] text-[#00ffa3] rounded px-2 py-1 text-[10px]" />
                  </div>
                  <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                      Opacidade Etiqueta (Data)
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#00ffa3] font-bold">{Math.round((timeLabelsOpacity || 0) * 100)}%</span>
                      <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ['timeLabelsOpacity']: !(p['timeLabelsOpacity'] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                        {(lockedSliders['timeLabelsOpacity'] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                      </button>
                    </div>
                  </div>
<input type="range" min="0" max="1" step="0.01" value={timeLabelsOpacity} onChange={(e) => setTimeLabelsOpacity(parseFloat(e.target.value))} disabled={lockedSliders['timeLabelsOpacity'] ?? false} className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['timeLabelsOpacity'] ?? false) ? "opacity-50 cursor-not-allowed" : "")} />
                </div>
                </div>

                
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">Tamanho Etiqueta (Preço)</span>
                    <input type="number" value={priceLabelsSize} onChange={(e) => setPriceLabelsSize(parseFloat(e.target.value))} className="w-full bg-[#111] text-[#00ffa3] rounded px-2 py-1 text-[10px]" />
                  </div>
                  <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                      Opacidade Etiqueta (Preço)
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#00ffa3] font-bold">{Math.round((priceLabelsOpacity || 0) * 100)}%</span>
                      <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ["priceLabelsOpacity"]: !(p["priceLabelsOpacity"] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                        {(lockedSliders["priceLabelsOpacity"] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                      </button>
                    </div>
                  </div>
<input type="range" min="0" max="1" step="0.01" value={priceLabelsOpacity} onChange={(e) => setPriceLabelsOpacity(parseFloat(e.target.value))} disabled={lockedSliders["priceLabelsOpacity"] ?? false} className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders["priceLabelsOpacity"] ?? false) ? "opacity-50 cursor-not-allowed" : "")} />
                </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">Tamanho Etiqueta (Força)</span>
                    <input type="number" value={momentumLabelsSize} onChange={(e) => setMomentumLabelsSize(parseFloat(e.target.value))} className="w-full bg-[#111] text-[#00ffa3] rounded px-2 py-1 text-[10px]" />
                  </div>
                  <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                      Opacidade Etiqueta (Força)
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#00ffa3] font-bold">{Math.round((momentumLabelsOpacity || 0) * 100)}%</span>
                      <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ["momentumLabelsOpacity"]: !(p["momentumLabelsOpacity"] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                        {(lockedSliders["momentumLabelsOpacity"] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                      </button>
                    </div>
                  </div>
<input type="range" min="0" max="1" step="0.01" value={momentumLabelsOpacity} onChange={(e) => setMomentumLabelsOpacity(parseFloat(e.target.value))} disabled={lockedSliders["momentumLabelsOpacity"] ?? false} className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders["momentumLabelsOpacity"] ?? false) ? "opacity-50 cursor-not-allowed" : "")} />
                </div>
                </div>

                {/* Conexões (Edges) */}
                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                      Mostrar Conexões (Edges)
                    </span>
                    <div className="flex items-center gap-2">
                      <input 
                        type="checkbox" 
                        checked={showConnectors} 
                        onChange={(e) => setShowConnectors(e.target.checked)} 
                        disabled={lockedSliders['box_connectors'] ?? false}
                        className="rounded bg-[#111] border-white/10 text-[#00ffa3] focus:ring-[#00ffa3]/20"
                      />
                      <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ['box_connectors']: !(p['box_connectors'] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                        {(lockedSliders['box_connectors'] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                      </button>
                    </div>
                  </div>
                </div>

                {showConnectors && (
                  <>
                    <div className="space-y-1">
                      <div className="flex justify-between items-center">
                        <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                          Opacidade das Conexões
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] text-[#00ffa3] font-bold">{Math.round((connectorsOpacity || 0) * 100)}%</span>
                        </div>
                      </div>
                      <input 
                        type="range" 
                        min="0" 
                        max="1" 
                        step="0.05" 
                        value={connectorsOpacity} 
                        onChange={(e) => setConnectorsOpacity(parseFloat(e.target.value))} 
                        disabled={lockedSliders['box_connectors'] ?? false} 
                        className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['box_connectors'] ?? false) ? "opacity-50 cursor-not-allowed" : "")} 
                      />
                    </div>

                    <div className="space-y-1">
                      <div className="flex justify-between items-center">
                        <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                          Espessura das Conexões
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] text-[#00ffa3] font-bold">{connectorsThickness?.toFixed(1) || "1.0"}px</span>
                        </div>
                      </div>
                      <input 
                        type="range" 
                        min="0.1" 
                        max="5" 
                        step="0.1" 
                        value={connectorsThickness} 
                        onChange={(e) => setConnectorsThickness(parseFloat(e.target.value))} 
                        disabled={lockedSliders['box_connectors'] ?? false} 
                        className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['box_connectors'] ?? false) ? "opacity-50 cursor-not-allowed" : "")} 
                      />
                    </div>
                  </>
                )}

                <div className="border border-[#00ffa3]/10 bg-[#05070e] rounded p-3 space-y-2">
                  <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px] block">Persistência em Nuvem</span>
                  {isAdmin ? (
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-1.5 text-[#00ffa3] text-[9px] font-bold">
                        <ShieldCheck size={12} />
                        ADMIN CONECTADO // SYNC OK
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-[8px] text-[#888] leading-normal">
                        Você está autenticado localmente. Para persistir as diretrizes na nuvem de forma que atualize todos os usuários instantaneamente, autentique sua Conta Google Admin.
                      </p>
                      <button
                        onClick={async () => {
                          try {
                            await loginWithGoogle();
                          } catch (err) {
                            console.error(err);
                          }
                        }}
                        className="w-full bg-[#00ffa3]/10 border border-[#00ffa3]/30 hover:bg-[#00ffa3]/20 text-[#00ffa3] py-1.5 text-[8px] uppercase font-black tracking-[1px] rounded transition-all cursor-pointer"
                      >
                        Vincular Conta Admin Google
                      </button>
                    </div>
                  )}
                </div>
              </div>
            
    </>
  );
}

function SpatialTab({ auth, isAdmin, isCreator }: { auth: NonNullable<GodPanelProps["auth"]>, isAdmin: boolean, isCreator: boolean }) {
  const {
    adminMetrics, leadsList, feedbacksList, expandedFeedbackId, setExpandedFeedbackId,
    isFeedbacksLoading, setFeedbackToDelete, systemUsersList,
    isSystemUsersLoading, systemUsersFilter, setSystemUsersFilter, userLevelFilter,
    setUserLevelFilter, showUserFilters, setShowUserFilters,
    setEditingSystemUser, expandedSystemUserId, setExpandedSystemUserId,
    activeSystemUserMenu, setActiveSystemUserMenu, showActivitiesForUserId,
    setShowActivitiesForUserId, setUserFeedbacksModalId,
    isSavingBrand, brandSaveSuccess, brandSaveError, fetchFeedbacks,
    fetchSystemUsers, fetchLeads, handleUpdateUserStatus,
    handleDeleteUser, handleDeleteLead, fetchAdminMetrics,
    isLeadsLoading
  } = auth || {};

  const controlRotateSpeed = useFlowaesStore(s => s.controlRotateSpeed);
  const controlPanSpeed = useFlowaesStore(s => s.controlPanSpeed);
  const controlZoomSpeed = useFlowaesStore(s => s.controlZoomSpeed);
  const setControlRotateSpeed = useFlowaesStore(s => s.setControlRotateSpeed);
  const setControlPanSpeed = useFlowaesStore(s => s.setControlPanSpeed);
  const setControlZoomSpeed = useFlowaesStore(s => s.setControlZoomSpeed);
  const candleGraphDistance3D = useFlowaesStore(s => s.candleGraphDistance3D);
  const floorDistance3D = useFlowaesStore(s => s.floorDistance3D);
  const sideGraphDistance3D = useFlowaesStore(s => s.sideGraphDistance3D);
  const width3D = useFlowaesStore(s => s.width3D);
  const heightScale3D = useFlowaesStore(s => s.heightScale3D);
  const zLimit3D = useFlowaesStore(s => s.zLimit3D);
  const setCandleGraphDistance3D = useFlowaesStore(s => s.setCandleGraphDistance3D);
  const setFloorDistance3D = useFlowaesStore(s => s.setFloorDistance3D);
  const setSideGraphDistance3D = useFlowaesStore(s => s.setSideGraphDistance3D);
  const setWidth3D = useFlowaesStore(s => s.setWidth3D);
  const setHeightScale3D = useFlowaesStore(s => s.setHeightScale3D);
  const setZLimit3D = useFlowaesStore(s => s.setZLimit3D);
  const lockedSliders = useFlowaesStore(s => s.lockedSliders);
  const setLockedSliders = useFlowaesStore(s => s.setLockedSliders);
  return (
    <>
      
<div className="space-y-4 animate-in fade-in duration-200">
                {/* Candle Graph Distance */}
                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                      Candles / Parede 2D
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#00ffa3] font-bold">{candleGraphDistance3D.toFixed(2)}x</span>
                      <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ['candleGraphDistance3D']: !(p['candleGraphDistance3D'] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                        {(lockedSliders['candleGraphDistance3D'] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                      </button>
                    </div>
                  </div>
                  <input
                    type="range"
                    min="0.1"
                    max="1.5"
                    step="0.05"
                    value={candleGraphDistance3D}
                    onChange={(e) => setCandleGraphDistance3D(parseFloat(e.target.value))}
                    disabled={lockedSliders['candleGraphDistance3D'] ?? false}
                    className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['candleGraphDistance3D'] ?? false) ? "opacity-50 cursor-not-allowed" : "")}
                  />
                  <p className="text-[7px] text-[#444] leading-relaxed">Distância do gráfico de fundo 2D em relação ao espaço 3D (Padrão: 0.35).</p>
                </div>

                {/* Floor Distance */}
                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                      Distância do Chão
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#00ffa3] font-bold">{floorDistance3D.toFixed(2)}x</span>
                      <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ['floorDistance3D']: !(p['floorDistance3D'] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                        {(lockedSliders['floorDistance3D'] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                      </button>
                    </div>
                  </div>
                  <input
                    type="range"
                    min="0.1"
                    max="1.0"
                    step="0.05"
                    value={floorDistance3D}
                    onChange={(e) => setFloorDistance3D(parseFloat(e.target.value))}
                    disabled={lockedSliders['floorDistance3D'] ?? false}
                    className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['floorDistance3D'] ?? false) ? "opacity-50 cursor-not-allowed" : "")}
                  />
                  <p className="text-[7px] text-[#444] leading-relaxed">Distância do piso do espaço 3D (Padrão: 0.25).</p>
                </div>

                {/* Side Graph Distance */}
                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                      Gráfico Lateral
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#00ffa3] font-bold">{sideGraphDistance3D.toFixed(2)}x</span>
                      <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ['sideGraphDistance3D']: !(p['sideGraphDistance3D'] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                        {(lockedSliders['sideGraphDistance3D'] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                      </button>
                    </div>
                  </div>
                  <input
                    type="range"
                    min="0.1"
                    max="0.8"
                    step="0.05"
                    value={sideGraphDistance3D}
                    onChange={(e) => setSideGraphDistance3D(parseFloat(e.target.value))}
                    disabled={lockedSliders['sideGraphDistance3D'] ?? false}
                    className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['sideGraphDistance3D'] ?? false) ? "opacity-50 cursor-not-allowed" : "")}
                  />
                  <p className="text-[7px] text-[#444] leading-relaxed">Distância do gráfico de profundidade lateral (Padrão: 0.40).</p>
                </div>

                {/* Width 3D */}
                <div className="space-y-1 pt-2 border-t border-white/5">
                  <div className="flex justify-between items-center">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                      Largura do Espaço (X)
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#00ffa3] font-bold">{width3D}px</span>
                      <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ['width3D']: !(p['width3D'] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                        {(lockedSliders['width3D'] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                      </button>
                    </div>
                  </div>
                  <input
                    type="range"
                    min="200"
                    max="10000"
                    step="10"
                    value={width3D}
                    onChange={(e) => setWidth3D(parseInt(e.target.value))}
                    disabled={lockedSliders['width3D'] ?? false}
                    className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['width3D'] ?? false) ? "opacity-50 cursor-not-allowed" : "")}
                  />
                  <p className="text-[7px] text-[#444] leading-relaxed">Proporção horizontal do gráfico de 3D (Padrão: 500px).</p>
                </div>

                {/* Height 3D */}
                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                      Altura do Espaço (Y)
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#00ffa3] font-bold">{heightScale3D}px</span>
                      <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ['heightScale3D']: !(p['heightScale3D'] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                        {(lockedSliders['heightScale3D'] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                      </button>
                    </div>
                  </div>
                  <input
                    type="range"
                    min="150"
                    max="5000"
                    step="10"
                    value={heightScale3D}
                    onChange={(e) => setHeightScale3D(parseInt(e.target.value))}
                    disabled={lockedSliders['heightScale3D'] ?? false}
                    className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['heightScale3D'] ?? false) ? "opacity-50 cursor-not-allowed" : "")}
                  />
                  <p className="text-[7px] text-[#444] leading-relaxed">Proporção vertical/escala do gráfico de preço 3D (Padrão: 350px).</p>
                </div>

                {/* Depth 3D */}
                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                      Profundidade do Espaço (Z)
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#00ffa3] font-bold">{zLimit3D}px</span>
                      <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ['zLimit3D']: !(p['zLimit3D'] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                        {(lockedSliders['zLimit3D'] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                      </button>
                    </div>
                  </div>
                  <input
                    type="range"
                    min="100"
                    max="5000"
                    step="10"
                    value={zLimit3D}
                    onChange={(e) => setZLimit3D(parseInt(e.target.value))}
                    disabled={lockedSliders['zLimit3D'] ?? false}
                    className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['zLimit3D'] ?? false) ? "opacity-50 cursor-not-allowed" : "")}
                  />
                  <p className="text-[7px] text-[#444] leading-relaxed">Amplitude de oscilação / profundidade no eixo Z (Padrão: 200px).</p>
                </div>
                
                {/* Control Speeds */}
                <div className="space-y-1 pt-2 border-t border-white/5">
                  <div className="flex justify-between items-center">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                      Velocidade de Rotação (Orbit)
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#00ffa3] font-bold">{controlRotateSpeed?.toFixed(2) || "1.00"}x</span>
                      <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ['controlRotateSpeed']: !(p['controlRotateSpeed'] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                        {(lockedSliders['controlRotateSpeed'] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                      </button>
                    </div>
                  </div>
                  <input
                    type="range"
                    min="0.1"
                    max="3.0"
                    step="0.1"
                    value={controlRotateSpeed}
                    onChange={(e) => setControlRotateSpeed(parseFloat(e.target.value))}
                    disabled={lockedSliders['controlRotateSpeed'] ?? false}
                    className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['controlRotateSpeed'] ?? false) ? "opacity-50 cursor-not-allowed" : "")}
                  />
                </div>

                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                      Velocidade de Movimento (Pan)
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#00ffa3] font-bold">{controlPanSpeed?.toFixed(2) || "1.00"}x</span>
                      <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ['controlPanSpeed']: !(p['controlPanSpeed'] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                        {(lockedSliders['controlPanSpeed'] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                      </button>
                    </div>
                  </div>
                  <input
                    type="range"
                    min="0.1"
                    max="5.0"
                    step="0.1"
                    value={controlPanSpeed}
                    onChange={(e) => setControlPanSpeed(parseFloat(e.target.value))}
                    disabled={lockedSliders['controlPanSpeed'] ?? false}
                    className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['controlPanSpeed'] ?? false) ? "opacity-50 cursor-not-allowed" : "")}
                  />
                </div>

                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-[8px] uppercase font-bold text-[#666666] tracking-[1px]">
                      Velocidade de Zoom
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#00ffa3] font-bold">{controlZoomSpeed?.toFixed(2) || "1.00"}x</span>
                      <button onClick={(e) => { e.stopPropagation(); setLockedSliders(p => ({...p, ['controlZoomSpeed']: !(p['controlZoomSpeed'] ?? false)})) }} className="text-[#00ffa3]/50 hover:text-[#00ffa3] focus:outline-none transition-all">
                        {(lockedSliders['controlZoomSpeed'] ?? false) ? <Lock size={14} /> : <Unlock size={14} />}
                      </button>
                    </div>
                  </div>
                  <input
                    type="range"
                    min="0.1"
                    max="5.0"
                    step="0.1"
                    value={controlZoomSpeed}
                    onChange={(e) => setControlZoomSpeed(parseFloat(e.target.value))}
                    disabled={lockedSliders['controlZoomSpeed'] ?? false}
                    className={cn("w-full h-1 bg-[#111] rounded appearance-none cursor-pointer accent-[#00ffa3]", (lockedSliders['controlZoomSpeed'] ?? false) ? "opacity-50 cursor-not-allowed" : "")}
                  />
                </div>
              </div>
            
    </>
  );
}

function UsersTab({ auth, isAdmin, isCreator }: { auth: NonNullable<GodPanelProps["auth"]>, isAdmin: boolean, isCreator: boolean }) {
  const {
    adminMetrics, leadsList, feedbacksList, expandedFeedbackId, setExpandedFeedbackId,
    isFeedbacksLoading, setFeedbackToDelete, systemUsersList,
    isSystemUsersLoading, systemUsersFilter, setSystemUsersFilter, userLevelFilter,
    setUserLevelFilter, showUserFilters, setShowUserFilters,
    setEditingSystemUser, expandedSystemUserId, setExpandedSystemUserId,
    activeSystemUserMenu, setActiveSystemUserMenu, showActivitiesForUserId,
    setShowActivitiesForUserId, setUserFeedbacksModalId,
    isSavingBrand, brandSaveSuccess, brandSaveError, fetchFeedbacks,
    fetchSystemUsers, fetchLeads, handleUpdateUserStatus,
    handleDeleteUser, handleDeleteLead, fetchAdminMetrics,
    isLeadsLoading
  } = auth || {};
return (
    <>
      
<div className="animate-in fade-in duration-200 flex flex-col flex-1 h-full">
                <div className="flex flex-col h-full">
                  <h2 className="text-[10px] uppercase tracking-[2px] text-[#00ffa3] border-b border-[#00ffa3]/20 pb-1 mb-2 flex justify-between items-center text-left shrink-0">
                    Base de Usuários
                    <button 
                      onClick={() => {
                        try {
                          const combined = [
                            ...systemUsersList.map(u => ({ ...u, type: 'user' })),
                            ...leadsList.map(l => ({ ...l, type: 'lead' }))
                          ];
                          const filtered = combined.filter(u => {
                            if (userLevelFilter === 'leads') {
                              const isLead = u.type === 'lead';
                              const isPendingUser = u.type === 'user' && u.subscription_status === 'pending';
                              const isBlockedUser = u.type === 'user' && u.subscription_status === 'blocked';
                              if (!isLead && !isPendingUser && !isBlockedUser) return false;
                            }
                            if (userLevelFilter === 'active' && (u.type !== 'user' || u.subscription_status !== 'active')) return false;
                            if (userLevelFilter === 'blocked' && (u.type !== 'user' || u.subscription_status !== 'blocked')) return false;
                            const term = systemUsersFilter.toLowerCase();
                            if (!term) return true;
                            return (u.name || '').toLowerCase().includes(term) || 
                                   (u.email || '').toLowerCase().includes(term) || 
                                   (u.phone || '').toLowerCase().includes(term) ||
                                   (u.cpf || '').toLowerCase().includes(term) ||
                                   (u.company || '').toLowerCase().includes(term) ||
                                   (u.subscription_status || '').toLowerCase().includes(term);
                          });
                          const csv = [
                            ['Tipo', 'Nome', 'Email', 'Telefone', 'CPF/Empresa', 'Status', 'Papel', 'Data de Inscrição'],
                            ...filtered.map(u => {
                              if (u.type === 'lead') {
                                return ['Lead', u.name || '', u.email || '', u.phone || '', u.company || '', 'Lead', 'Interessado', u.createdAt || ''];
                              }
                              return [
                                'Usuário', u.name || '', u.email || '', u.phone || '', u.cpf || '', u.subscription_status || 'pending', isAdminEmailLabel(u.email) ? 'Admin' : 'Usuário', u.createdAt || ''
                              ];
                            })
                          ].map(e => e.join(",")).join("\n");
                          const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = 'usuarios-e-leads.csv';
                          a.click();
                        } catch (e) {
                          alert('Erro ao exportar usuários');
                        }
                      }}
                      className="bg-[#00ffa3]/20 text-[#00ffa3] px-2 py-1 rounded border border-[#00ffa3]/40 hover:bg-[#00ffa3]/30 transition-all flex items-center gap-1 cursor-pointer font-mono"
                    >
                      <TrendingUp size={10} /> Exportar CSV
                    </button>
                  </h2>
                  <div className="text-[9px] text-[#888] mb-3 leading-relaxed flex justify-between items-center gap-2 text-left shrink-0">
                    <span>Leads, Fila de espera e Pioneiros.</span>
                    <span className="font-mono text-[#00ffa3]">{systemUsersList.length + leadsList.length} total</span>
                  </div>

                  {/* Filter Controls */}
                  <div className="flex items-center gap-2 mb-3 shrink-0">
                    <div className="relative flex-1">
                      <input 
                        type="text"
                        placeholder="Filtrar por nome, email, cpf..."
                        value={systemUsersFilter}
                        onChange={(e) => setSystemUsersFilter(e.target.value)}
                        className="w-full bg-[#03050f] border border-[#00ffa3]/30 rounded px-2 py-1.5 pl-7 text-[10px] font-mono text-[#e0e0e0] focus:outline-none focus:border-[#00ffa3]/60 placeholder:text-gray-700"
                      />
                      <Search size={12} className="absolute left-2 top-1/2 transform -translate-y-1/2 text-[#00ffa3]/50" />
                    </div>
                    <button
                      onClick={() => setShowUserFilters(!showUserFilters)}
                      className={cn(
                        "p-1.5 rounded border transition-all flex items-center justify-center",
                        showUserFilters || userLevelFilter !== 'all' 
                          ? "bg-[#00ffa3]/20 border-[#00ffa3]/50 text-[#00ffa3]" 
                          : "bg-black/40 border-white/10 text-[#666] hover:text-white hover:border-white/30"
                      )}
                    >
                      <Filter size={14} />
                    </button>
                    <button
                      onClick={() => {
                        fetchSystemUsers();
                        fetchLeads();
                      }}
                      className="p-1.5 rounded border border-white/10 text-[#666] bg-black/40 hover:text-white hover:border-white/30 transition-all flex items-center justify-center"
                      title="Atualizar"
                    >
                      <RefreshCcw size={14} />
                    </button>
                  </div>

                  {showUserFilters && (
                    <div className="flex gap-1 mb-3 bg-black/40 p-1 rounded border border-[#00ffa3]/20 shrink-0 overflow-x-auto custom-scrollbar animate-in slide-in-from-top-1 duration-200">
                      {['all', 'leads', 'active', 'blocked'].map(level => {
                         const label = ({ 'all': 'TODOS', 'leads': 'LEADS', 'active': 'FUNDADORES', 'blocked': 'BLOQUEADOS' } as Record<string, string>)[level];
                         return (
                           <button
                             key={level}
                             onClick={() => setUserLevelFilter(level as any)}
                             className={cn(
                               "px-2 py-1 text-[8px] font-mono rounded font-bold transition-all whitespace-nowrap",
                               userLevelFilter === level 
                                 ? "bg-[#00ffa3]/20 text-[#00ffa3] border border-[#00ffa3]/40" 
                                 : "text-[#666] hover:text-white border border-transparent"
                             )}
                           >
                             {label}
                           </button>
                         );
                      })}
                    </div>
                  )}

                  {/* Users Table List */}
                  <div className="bg-[#0a0e27]/50 border border-[#00ffa3]/20 rounded-lg overflow-hidden flex flex-col flex-1 min-h-0 mb-4">
                    {(isSystemUsersLoading || isLeadsLoading) ? (
                      <div className="p-10 text-center flex-1 flex flex-col justify-center">
                        <Activity className="w-6 h-6 mx-auto text-[#00ffa3] animate-spin mb-2" />
                        <p className="text-[10px] text-[#666] uppercase tracking-[1px]">Carregando Dados...</p>
                      </div>
                    ) : (
                      <div className="overflow-y-auto divide-y divide-[#00ffa3]/10 text-left custom-scrollbar flex-1 min-h-0">
                        {(() => {
                          const combined = [
                            ...systemUsersList.map(u => ({ ...u, type: 'user', sortDate: new Date(u.createdAt || 0).getTime() })),
                            ...leadsList.map(l => ({ ...l, type: 'lead', sortDate: new Date(l.createdAt || 0).getTime() }))
                          ].sort((a, b) => b.sortDate - a.sortDate);

                          const filtered = combined.filter(u => {
                            // Filter by Level Tab
                            // Filter by Level Tab
                            if (userLevelFilter === 'leads') {
                              const isLead = u.type === 'lead';
                              const isPendingUser = u.type === 'user' && u.subscription_status === 'pending';
                              const isBlockedUser = u.type === 'user' && u.subscription_status === 'blocked';
                              if (!isLead && !isPendingUser && !isBlockedUser) return false;
                            }
                            if (userLevelFilter === 'active' && (u.type !== 'user' || u.subscription_status !== 'active')) return false;
                            if (userLevelFilter === 'blocked' && (u.type !== 'user' || u.subscription_status !== 'blocked')) return false;

                            // Filter by Text Input
                            const term = systemUsersFilter.toLowerCase();
                            if (!term) return true;

                            return (u.name || '').toLowerCase().includes(term) || 
                                   (u.email || '').toLowerCase().includes(term) || 
                                   (u.phone || '').toLowerCase().includes(term) ||
                                   (u.cpf || '').toLowerCase().includes(term) ||
                                   (u.company || '').toLowerCase().includes(term) ||
                                   (u.subscription_status || '').toLowerCase().includes(term);
                          });

                          if (filtered.length === 0) {
                            return (
                              <div className="p-8 text-center text-[#666] text-[10px] uppercase tracking-[1px]">
                                Nenhum registro encontrado
                              </div>
                            );
                          }

                          return filtered.map((u) => {
                            const isExpanded = expandedSystemUserId === (u.uid || u.id);
                            const isMenuOpen = activeSystemUserMenu === (u.uid || u.id);
                            const isLead = u.type === 'lead';
                            
                            const getTags = () => {
                              const tags = [];
                              if (isLead) {
                                tags.push({ label: 'LEAD', color: 'bg-purple-500/20 text-purple-400' });
                              } else {
                                if (u.subscription_status === 'active') {
                                  tags.push({ label: 'FUNDADOR', color: 'bg-[#00ffa3]/20 text-[#00ffa3]' });
                                } else if (u.subscription_status === 'blocked') {
                                  tags.push({ label: 'BLOQUEADO', color: 'bg-red-500/20 text-red-500' });
                                  tags.push({ label: 'LEAD', color: 'bg-purple-500/20 text-purple-400' });
                                } else if (u.subscription_status === 'pending') {
                                  tags.push({ label: 'REVOGADO', color: 'bg-yellow-500/20 text-yellow-500' });
                                  tags.push({ label: 'LEAD', color: 'bg-purple-500/20 text-purple-400' });
                                } else {
                                  tags.push({ label: u.subscription_status || 'LEAD', color: 'bg-gray-500/20 text-gray-400' });
                                }
                              }
                              return tags;
                            };
                            const tags = getTags();

                            return (
                              <div key={u.type + '-' + (u.uid || u.id)} className="p-2.5 text-[9px] hover:bg-[#00ffa3]/5 transition-all space-y-1 relative">
                                <div className="flex justify-between items-center text-left cursor-pointer" onClick={() => setExpandedSystemUserId(isExpanded ? null : (u.uid || u.id))}>
                                  <div className="flex flex-col">
                                    <span className="font-bold text-[#e0e0e0] flex items-center gap-1">
                                      {u.name || 'Sem nome'}
                                      {(isAdminEmailLabel(u.email) || u.role === 'admin') && <span className="text-yellow-500 text-[8px] font-bold">[ADM]</span>}
                                      {u.role === 'staff' && <span className="text-yellow-500 text-[8px] font-bold">[ADM]</span>}
                                    </span>

                                    <span className="text-[#00ffa3]/50 font-mono text-[8px]">{isLead ? 'Empresa:' : 'CPF:'} {u.company || u.cpf || 'N/A'}</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    {tags.map((tag, idx) => (
                                      <span key={idx} className={cn("text-[8px] font-mono px-1 rounded uppercase", tag.color)}>
                                        {tag.label}
                                      </span>
                                    ))}
                                    <button 
                                      onClick={(e) => { 
                                        e.stopPropagation(); 
                                        const targetId = u.uid || u.id;
                                        setActiveSystemUserMenu(activeSystemUserMenu === targetId ? null : targetId); 
                                      }} 
                                      className="text-[#888] hover:text-[#00ffa3] p-1 border border-white/10 rounded"
                                    >
                                      <MoreHorizontal size={10} />
                                    </button>
                                  </div>
                                </div>

                                {isExpanded && (
                                  <div className="pt-2 mt-2 border-t border-[#00ffa3]/10 text-[#888] space-y-1">
                                    <p className="truncate"><span className="text-white/40">Email:</span> {u.email || <span className="text-white/30 italic">sem e-mail (preenchido no próximo login)</span>}</p>
                                    <p><span className="text-white/40">Telefone:</span> {u.phone || 'N/A'}</p>
                                    <p><span className="text-white/40">Data de Cadastro:</span> {new Date(u.createdAt || 0).toLocaleDateString('pt-BR')}</p>
                                    {!isLead && u.license_key && (
                                      <p><span className="text-white/40">Chave de Licença:</span> <span className="text-[#00d9ff]">{u.license_key}</span></p>
                                    )}
                                    {u.qualification && (
                                      <div className="bg-black/20 p-1.5 rounded border border-white/5 space-y-0.5 my-1 text-[8px]">
                                        <p className="text-[#00ffa3]/80 font-bold uppercase tracking-wider">Qualificação:</p>
                                        <p><span className="text-white/40">Capital Estimado:</span> {u.qualification.capital || 'Não informado'}</p>
                                        <p><span className="text-white/40">Experiência:</span> {u.qualification.experience || 'Não informado'}</p>
                                      </div>
                                    )}
                                    {isLead && u.details && (
                                      <div className="mt-1 bg-black/40 p-1.5 rounded text-[8px] text-[#777] border border-white/5">
                                        {u.details}
                                      </div>
                                    )}
                                    
                                    {!isLead && (
                                      <div className="mt-2 border-t border-[#00ffa3]/10 pt-2 flex flex-col gap-1.5">
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const targetId = u.uid || u.id;
                                            setUserFeedbacksModalId(targetId);
                                          }}
                                          className="w-full py-1.5 px-3 rounded border border-[#00d9ff]/20 bg-[#00d9ff]/5 text-[#00d9ff] hover:bg-[#00d9ff]/10 font-bold text-[8px] uppercase tracking-wider flex items-center justify-between transition-colors"
                                        >
                                          <div className="flex items-center gap-1.5">
                                            <MessageSquarePlus size={12} /> Visualizar Feedbacks
                                          </div>
                                          <span className="bg-[#00d9ff]/20 px-1.5 py-0.5 rounded text-[7px]">
                                            {feedbacksList.filter(f => f.userId === (u.uid || u.id)).length} Envio(s)
                                          </span>
                                        </button>
                                        <button
                                          onClick={(e) => { 
                                             e.stopPropagation();
                                            const targetId = u.uid || u.id;
                                            setShowActivitiesForUserId(showActivitiesForUserId === targetId ? null : targetId); 
                                           }}
                                          className="w-full py-1.5 px-3 rounded border border-[#00ffa3]/20 bg-[#00ffa3]/5 text-[#00ffa3] hover:bg-[#00ffa3]/10 font-bold text-[8px] uppercase tracking-wider flex items-center justify-center gap-1.5 transition-colors"
                                        >
                                          📋 {showActivitiesForUserId === (u.uid || u.id) ? 'Ocultar Atividades' : 'Histórico de Atividades'}
                                        </button>
                                        {showActivitiesForUserId === (u.uid || u.id) && (
                                          <div className="mt-2 space-y-1">
                                            {u.activities && u.activities.length > 0 ? (
                                              <div className="max-h-32 overflow-y-auto divide-y divide-[#00ffa3]/10 bg-black/30 p-1.5 rounded border border-[#00ffa3]/5 font-mono text-[7.5px] text-white/70 space-y-1">
                                                {u.activities.map((act: any, aIdx: number) => (
                                                  <div key={aIdx} className="py-1 text-left">
                                                    <div className="flex justify-between text-[#888] text-[7px] mb-0.5">
                                                      <span>{new Date(act.timestamp).toLocaleString('pt-BR')}</span>
                                                      <span className="text-teal-400">Por: {act.by || 'Sistema'}</span>
                                                    </div>
                                                    <p className="text-[#e0e0e0] font-sans leading-relaxed">{act.description}</p>
                                                  </div>
                                                ))}
                                              </div>
                                            ) : (
                                              <p className="text-[7.5px] italic text-[#666] py-1 text-center">Nenhuma atividade registrada.</p>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Admin Action Menu for Users & Leads */}
                                {isMenuOpen && (
                                  <div className="absolute right-2 top-8 bg-[#0a0e27] border border-white/20 rounded-lg p-1 z-10 flex flex-col gap-1 min-w-[120px] shadow-xl animate-in zoom-in-95 duration-100">
                                    {isLead ? (
                                      <>
                                        <button 
                                          onClick={(e) => { e.stopPropagation(); setEditingSystemUser(u); setActiveSystemUserMenu(null); }}
                                          className="w-full text-left bg-white/5 hover:bg-white/10 text-white text-[8px] py-1.5 px-2 rounded transition-colors"
                                        >
                                          EDITAR
                                        </button>
                                        <button 
                                          onClick={(e) => { e.stopPropagation(); handleDeleteLead(u.id); setActiveSystemUserMenu(null); }}
                                          className="w-full text-left bg-red-500/10 hover:bg-red-500/20 text-red-400 text-[8px] py-1.5 px-2 rounded transition-colors mt-1"
                                        >
                                          EXCLUIR
                                        </button>
                                      </>
                                    ) : (
                                      <>
                                        <button 
                                          onClick={(e) => { e.stopPropagation(); setEditingSystemUser(u); setActiveSystemUserMenu(null); }}
                                          className="w-full text-left bg-white/5 hover:bg-white/10 text-white text-[8px] py-1.5 px-2 rounded transition-colors"
                                        >
                                          EDITAR
                                        </button>
                                        
                                        {u.subscription_status === 'active' ? (
                                          <button 
                                            onClick={(e) => { e.stopPropagation(); handleUpdateUserStatus(u.uid, 'pending', u); setActiveSystemUserMenu(null); }}
                                            className="w-full text-left bg-yellow-500/10 hover:bg-yellow-500/20 text-yellow-500 text-[8px] py-1.5 px-2 rounded transition-colors"
                                          >
                                            REVOGAR
                                          </button>
                                        ) : (
                                          <button 
                                            onClick={(e) => { e.stopPropagation(); handleUpdateUserStatus(u.uid, 'active', u); setActiveSystemUserMenu(null); }}
                                            className="w-full text-left bg-[#00ffa3]/10 hover:bg-[#00ffa3]/20 text-[#00ffa3] text-[8px] py-1.5 px-2 rounded transition-colors"
                                          >
                                            APROVAR
                                          </button>
                                        )}
                                        
                                        {u.role === 'staff' ? (
                                          <button 
                                            onClick={(e) => { e.stopPropagation(); handleUpdateUserStatus(u.uid, 'remove_staff', u); setActiveSystemUserMenu(null); }}
                                            className="w-full text-left bg-purple-500/10 hover:bg-purple-500/20 text-purple-400 text-[8px] py-1.5 px-2 rounded transition-colors"
                                          >
                                            REMOVER COLABORADOR
                                          </button>
                                        ) : (
                                          <button 
                                            onClick={(e) => { e.stopPropagation(); handleUpdateUserStatus(u.uid, 'staff', u); setActiveSystemUserMenu(null); }}
                                            className="w-full text-left bg-purple-500/10 hover:bg-purple-500/20 text-purple-400 text-[8px] py-1.5 px-2 rounded transition-colors"
                                          >
                                            TORNAR COLABORADOR
                                          </button>
                                        )}

                                        {u.subscription_status === 'blocked' ? (
                                          <button 
                                            onClick={(e) => { e.stopPropagation(); handleUpdateUserStatus(u.uid, 'pending', u); setActiveSystemUserMenu(null); }}
                                            className="w-full text-left bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 text-[8px] py-1.5 px-2 rounded transition-colors"
                                          >
                                            DESBLOQUEAR
                                          </button>
                                        ) : (
                                          <button 
                                            onClick={(e) => { e.stopPropagation(); handleUpdateUserStatus(u.uid, 'blocked', u); setActiveSystemUserMenu(null); }}
                                            className="w-full text-left bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 text-[8px] py-1.5 px-2 rounded transition-colors"
                                          >
                                            {u.subscription_status === 'active' ? 'REVOGAR E BLOQUEAR' : 'BLOQUEAR'}
                                          </button>
                                        )}

                                        <button 
                                          onClick={(e) => { e.stopPropagation(); handleDeleteUser(u.uid); setActiveSystemUserMenu(null); }}
                                          className="w-full text-left bg-red-500/10 hover:bg-red-500/20 text-red-400 text-[8px] py-1.5 px-2 rounded transition-colors mt-1"
                                        >
                                          EXCLUIR
                                        </button>
                                      </>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          });
                        })()}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            
    </>
  );
}

function FeedbacksTab({ auth, isAdmin, isCreator }: { auth: NonNullable<GodPanelProps["auth"]>, isAdmin: boolean, isCreator: boolean }) {
  const {
    adminMetrics, leadsList, feedbacksList, expandedFeedbackId, setExpandedFeedbackId,
    isFeedbacksLoading, setFeedbackToDelete, systemUsersList,
    isSystemUsersLoading, systemUsersFilter, setSystemUsersFilter, userLevelFilter,
    setUserLevelFilter, showUserFilters, setShowUserFilters,
    setEditingSystemUser, expandedSystemUserId, setExpandedSystemUserId,
    activeSystemUserMenu, setActiveSystemUserMenu, showActivitiesForUserId,
    setShowActivitiesForUserId, setUserFeedbacksModalId,
    isSavingBrand, brandSaveSuccess, brandSaveError, fetchFeedbacks,
    fetchSystemUsers, fetchLeads, handleUpdateUserStatus,
    handleDeleteUser, handleDeleteLead, fetchAdminMetrics,
    isLeadsLoading
  } = auth || {};
return (
    <>
      
<div className="animate-in fade-in duration-200 flex flex-col flex-1 h-full">
                <div className="flex flex-col h-full">
                  <h2 className="text-[10px] uppercase tracking-[2px] text-[#00ffa3] border-b border-[#00ffa3]/20 pb-1 mb-2 flex justify-between items-center text-left shrink-0">
                    Relatório de Feedbacks
                    <button 
                      onClick={fetchFeedbacks}
                      className="p-1.5 rounded border border-white/10 text-[#666] bg-black/40 hover:text-white hover:border-white/30 transition-all flex items-center justify-center"
                      title="Atualizar"
                    >
                      <RefreshCcw size={14} />
                    </button>
                  </h2>
                  <div className="flex-1 overflow-y-auto min-h-0 pr-1 space-y-2">
                    {isFeedbacksLoading ? (
                      <div className="flex items-center justify-center h-20 text-[#666]">
                        <RefreshCcw size={16} className="animate-spin" />
                      </div>
                    ) : feedbacksList.length === 0 ? (
                      <div className="text-center py-8 text-[#666] text-[10px] uppercase tracking-wider">
                        Nenhum feedback recebido.
                      </div>
                    ) : (
                      feedbacksList.map(fb => {
                        const systemUser = systemUsersList.find(u => u.id === fb.userId);
                        const displayCpf = systemUser?.cpf || fb.userCpf;
                        const displayName = systemUser?.name || fb.userName || fb.userEmail || 'Desconhecido';

                        return (
                        <div 
                          key={fb.id} 
                          className={cn("bg-black/40 border rounded-lg p-3 transition-colors cursor-pointer", expandedFeedbackId === fb.id ? "border-[#00ffa3]/30" : "border-white/5 hover:border-white/20")}
                          onClick={() => setExpandedFeedbackId(expandedFeedbackId === fb.id ? null : fb.id)}
                        >
                          <div className="flex justify-between items-center mb-1">
                            <div className="text-[10px] text-[#00ffa3] font-bold truncate">
                              {displayName}
                            </div>
                            <div className="text-[8px] text-[#666]">
                              {new Date(fb.createdAt).toLocaleString()}
                            </div>
                          </div>
                          
                          {expandedFeedbackId === fb.id && (
                            <div className="mt-3 animate-in fade-in slide-in-from-top-2 duration-200">
                              <div className="mb-3 flex flex-col gap-1 border-b border-white/5 pb-3">
                                <span className="text-[8px] uppercase tracking-wider text-[#666]">Email</span>
                                <span className="text-[9px] font-mono text-white/80">{systemUser?.email || fb.userEmail || 'Desconhecido'}</span>
                                <span className="text-[8px] uppercase tracking-wider text-[#666] mt-1">ID do Usuário</span>
                                <span className="text-[9px] font-mono text-white/80">{fb.userId}</span>
                                {displayCpf && (
                                  <>
                                    <span className="text-[8px] uppercase tracking-wider text-[#666] mt-1">CPF</span>
                                    <span className="text-[9px] font-mono text-white/80">{displayCpf}</span>
                                  </>
                                )}
                              </div>
                              {fb.description && (
                                <p className="text-[9px] text-white/70 mb-3 bg-white/5 p-2 rounded">
                                  {fb.description}
                                </p>
                              )}
                              {(fb.imageUrl || fb.imageBase64) && (
                                <a href={fb.imageUrl || fb.imageBase64} target="_blank" rel="noopener noreferrer" className="block w-full border border-white/10 rounded overflow-hidden hover:border-[#00ffa3]/50 transition-colors relative group" onClick={(e) => e.stopPropagation()}>
                                  <img src={fb.imageUrl || fb.imageBase64} alt="Feedback" className="w-full object-contain max-h-40 bg-black/80" />
                                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                    <span className="text-[9px] text-[#00ffa3] font-bold uppercase tracking-wider bg-black/80 px-2 py-1 rounded border border-[#00ffa3]/30">Visualizar Completo</span>
                                  </div>
                                </a>
                              )}
                              
                              {isAdmin && (
                                <div className="mt-3 pt-3 border-t border-white/5 flex justify-end">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setFeedbackToDelete(fb.id); }}
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-500 border border-red-500/30 rounded text-[9px] font-bold uppercase tracking-wider transition-colors"
                                  >
                                    <Trash2 size={12} /> Excluir Feedback
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )})
                    )}
                  </div>
                </div>
              </div>
            
    </>
  );
}

function GeneralTab({ auth, isAdmin, isCreator }: { auth: NonNullable<GodPanelProps["auth"]>, isAdmin: boolean, isCreator: boolean }) {
  const show3D = useFlowaesStore(s => s.show3D);
  const backwallStyle = useFlowaesStore(s => s.backwallStyle);
  const setShow3D = useFlowaesStore(s => s.setShow3D);
  const setBackwallStyle = useFlowaesStore(s => s.setBackwallStyle);
  const showShadowFloor = useFlowaesStore(s => s.showShadowFloor);
  const setShowShadowFloor = useFlowaesStore(s => s.setShowShadowFloor);
  const showShadowSide = useFlowaesStore(s => s.showShadowSide);
  const setShowShadowSide = useFlowaesStore(s => s.setShowShadowSide);
  const showMidLineWall = useFlowaesStore(s => s.showMidLineWall);
  const setShowMidLineWall = useFlowaesStore(s => s.setShowMidLineWall);
  const midLineWallColor = useFlowaesStore(s => s.midLineWallColor);
  const setMidLineWallColor = useFlowaesStore(s => s.setMidLineWallColor);
  const midLineWallOpacity = useFlowaesStore(s => s.midLineWallOpacity);
  const setMidLineWallOpacity = useFlowaesStore(s => s.setMidLineWallOpacity);
  const sideHistoryShadowStrength = useFlowaesStore(s => s.sideHistoryShadowStrength);
  const setSideHistoryShadowStrength = useFlowaesStore(s => s.setSideHistoryShadowStrength);
  const historyShadowStrength = useFlowaesStore(s => s.historyShadowStrength);
  const setHistoryShadowStrength = useFlowaesStore(s => s.setHistoryShadowStrength);

  return (
    <div className="space-y-4 animate-in fade-in duration-200">
      <div className="flex flex-col gap-2">
        <label className="flex items-center gap-2 text-[10px] text-[#ccc] uppercase">
          <input type="checkbox" checked={show3D} onChange={(e) => setShow3D(e.target.checked)} className="accent-[#00ffa3]"/>
          Engine 3D Ativada
        </label>
        <label className="flex items-center gap-2 text-[10px] text-[#ccc] uppercase">
          <input type="checkbox" checked={showShadowFloor} onChange={(e) => setShowShadowFloor(e.target.checked)} className="accent-[#00ffa3]"/>
          Sombra no Chão
        </label>
        <label className="flex items-center gap-2 text-[10px] text-[#ccc] uppercase">
          <input type="checkbox" checked={showShadowSide} onChange={(e) => setShowShadowSide(e.target.checked)} className="accent-[#00ffa3]"/>
          Sombra Lateral
        </label>
        <label className="flex items-center gap-2 text-[10px] text-[#ccc] uppercase">
          <input type="checkbox" checked={showMidLineWall} onChange={(e) => setShowMidLineWall(e.target.checked)} className="accent-[#00ffa3]"/>
          Linha Central da Parede
        </label>
      </div>

      <div className="space-y-2">
        <h3 className="text-[#666] text-[10px] uppercase tracking-[2px]">Estilo da Parede Fundo</h3>
        <select value={backwallStyle} onChange={(e) => setBackwallStyle(e.target.value as any)} className="w-full bg-[#111] border border-white/10 text-[#00ffa3] text-xs p-2 rounded">
          <option value="solid">Sólido</option>
          <option value="wireframe">Wireframe</option>
          <option value="transparent">Transparente</option>
        </select>
      </div>

      <div className="space-y-2">
        <h3 className="text-[#666] text-[10px] uppercase tracking-[2px]">Força da Sombra Lateral ({sideHistoryShadowStrength.toFixed(1)})</h3>
        <input type="range" min="0" max="1" step="0.1" value={sideHistoryShadowStrength} onChange={(e) => setSideHistoryShadowStrength(parseFloat(e.target.value))} className="w-full accent-[#00ffa3]" />
      </div>

      <div className="space-y-2">
        <h3 className="text-[#666] text-[10px] uppercase tracking-[2px]">Força da Sombra História ({historyShadowStrength.toFixed(1)})</h3>
        <input type="range" min="0" max="1" step="0.1" value={historyShadowStrength} onChange={(e) => setHistoryShadowStrength(parseFloat(e.target.value))} className="w-full accent-[#00ffa3]" />
      </div>
      
      <div className="space-y-2">
        <h3 className="text-[#666] text-[10px] uppercase tracking-[2px]">Opacidade Linha Central ({midLineWallOpacity.toFixed(1)})</h3>
        <input type="range" min="0" max="1" step="0.1" value={midLineWallOpacity} onChange={(e) => setMidLineWallOpacity(parseFloat(e.target.value))} className="w-full accent-[#00ffa3]" />
      </div>
    </div>
  );
}


function DashboardTab({ auth, isAdmin, isCreator }: { auth: NonNullable<GodPanelProps["auth"]>, isAdmin: boolean, isCreator: boolean }) {
  
  const {
    adminMetrics, leadsList, feedbacksList, expandedFeedbackId, setExpandedFeedbackId,
    isFeedbacksLoading, setFeedbackToDelete, systemUsersList,
    isSystemUsersLoading, systemUsersFilter, setSystemUsersFilter, userLevelFilter,
    setUserLevelFilter, showUserFilters, setShowUserFilters,
    setEditingSystemUser, expandedSystemUserId, setExpandedSystemUserId,
    activeSystemUserMenu, setActiveSystemUserMenu, showActivitiesForUserId,
    setShowActivitiesForUserId, setUserFeedbacksModalId,
    isSavingBrand, brandSaveSuccess, brandSaveError, fetchFeedbacks,
    fetchSystemUsers, fetchLeads, handleUpdateUserStatus,
    handleDeleteUser, handleDeleteLead, fetchAdminMetrics,
    isLeadsLoading
  } = auth || {};

  const showPerfPanel = useFlowaesStore(s => s.showPerfPanel);
  const setShowPerfPanel = useFlowaesStore(s => s.setShowPerfPanel);

  return (
    <div className="space-y-4 animate-in fade-in duration-200">
      <div className="flex flex-col gap-4">

        
        <div className="flex items-center justify-between p-3.5 bg-[#05070e] rounded border border-white/10 hover:border-white/20 transition-all">
          <div className="flex flex-col gap-1 pr-3">
            <span className="text-[10px] uppercase tracking-[1px] text-[#00ffa3] font-bold">
              Painel de Performance
            </span>
            <span className="text-[9px] text-[#888] font-sans tracking-wide leading-tight">
              Métricas Three.js em tempo real (CPU, GPU, Draw Calls, Triângulos e FPS)
            </span>
          </div>
          
          <button
            type="button"
            role="switch"
            aria-checked={showPerfPanel}
            onClick={() => setShowPerfPanel(!showPerfPanel)}
            className={cn(
              "relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none",
              showPerfPanel ? "bg-[#00ffa3] shadow-[0_0_10px_rgba(0,255,163,0.4)]" : "bg-[#1f2438]"
            )}
          >
            <span
              className={cn(
                "pointer-events-none inline-block h-4 w-4 transform rounded-full transition duration-200 ease-in-out shadow-md",
                showPerfPanel ? "translate-x-4 bg-black" : "translate-x-0 bg-zinc-400"
              )}
            />
          </button>
        </div>
      </div>
    </div>
  );
}
